import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import Select from 'react-select';
import Toast from '../components/Popup/Toast';
import Modal from '../components/Popup/Modal';
import BusinessProcessFlow from '../components/Navigation/BusinessProcessFlow';
import TasksDisplay from '../components/TasksDisplay';
import Lifecycle from '../components/Lifecycle';
import AttachmentsUpload from '../components/AttachmentsUpload';
import DocumentsDisplay from '../components/DocumentsDisplay';
import RiskAssessmentTimeline from '../components/RiskAssessmentTimeline';
import CustomFieldsRenderer from '../components/FieldRenderer/CustomFieldsRenderer';
import ExpandableDescription from '../components/ExpandableDescription';
import FieldRenderer from '../components/FieldRenderer';
import YesNoToggle from '../components/YesNoToggle';
import CreateTaskModal from '../components/CreateTaskModal';
import RiskMatrixModal from '../components/RiskMatrixModal';
import ProbabilityScaleModal from '../components/ProbabilityScaleModal';
import { useWorkflowStages, useStageFieldMappings, useDeleteItem, useUpdateItem, useItem, useCreateItem, useAdvanceItemStage, useRevertItemStage, useRiskMatrix, useTasks, useTasksByParent, useApprovalRequirements, useEligibleApprovers, useCreateApproval, useDeleteApproval, useRoleDefinitions, useCurrentUser, useApprovals, useCustomFieldValues, useUpdateCustomFieldValue, useCustomFields, useUsers, useDeleteRiskSnapshot, useRiskSnapshots, useItems, useUserRoles } from '../hooks/useQueries';
import { useFieldConfigs } from '../hooks/useFieldConfigs';
import { useHasRole } from '../hooks/useHasRole';
import { useIsMobile } from '../hooks/useIsMobile';
import { formatDisplayValue } from '../config/fieldConfig';
import { formatItemId, formatItemRef } from '../utils/itemIdFormat';
import './itemDetailPage.css';

// Standard item columns a draft can carry into POST /api/items. Anything not in
// this list has no home on create, so the draft locks it rather than accept an
// edit that Save would drop. Keep in sync with the insert in server/routes/items.js.
const DRAFT_PERSISTED_FIELDS = [
  'title',
  'description',
  'initial_notes',
  'current_assessment_notes',
  'immediate_actions_taken',
  'justification',
  'management_comments',
  'root_cause_title',
  'root_cause_analysis',
  'owner',
  'primary_owner',
  'secondary_owner',
  'category_id',
  'subcategory_id',
  'project_id'
];

// Owner fields whose assignment is confirmed in a modal and emails the new owner.
const OWNER_FIELD_IDS = ['primary_owner', 'secondary_owner'];

// Fields only an endorser (or admin) may set, in any stage.
const ENDORSEMENT_FIELD_IDS = ['endorser', 'endorsement_notes'];

// Lower-cased name of the stage where the owner fields become endorser-only.
const ENDORSE_STAGE_NAME = 'endorse';

// Matches the sizing of Modal's built-in footer buttons, which the owner
// assignment modal replaces to offer two ways to confirm.
const ownerAssignmentBtnStyle = {
  fontWeight: '500',
  padding: '0.75rem 1.5rem',
  borderRadius: '8px',
  fontSize: '0.875rem'
};

// `isDraft` renders the same page for an item that does not exist yet: every
// edit is buffered locally and nothing is written until Save. That is what keeps
// an abandoned "New Item" from becoming a permanent row.
const ItemDetailPage = ({ isDraft = false }) => {
  const { id: routeId } = useParams();
  // No id in draft mode, which also disables every `enabled: !!id` query below.
  const id = isDraft ? undefined : routeId;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const isMobile = useIsMobile();
  
  // Get source for back navigation
  const source = searchParams.get('source');
  const sourceTaskId = searchParams.get('sourceTaskId');
  const sourceApprovalId = searchParams.get('sourceApprovalId');
  
  // Create back navigation handler
  const handleBackNavigation = useCallback(() => {
    switch (source) {
      case 'reports':
        navigate('/reports');
        break;
      case 'tasks':
        navigate('/tasks');
        break;
      case 'task':
        if (sourceTaskId) {
          navigate(`/tasks/${sourceTaskId}`);
        } else {
          navigate('/tasks');
        }
        break;
      case 'approvals':
        navigate('/approvals');
        break;
      case 'approval':
        if (sourceApprovalId) {
          navigate(`/approvals/${sourceApprovalId}`);
        } else {
          navigate('/approvals');
        }
        break;
      default:
        navigate('/items');
        break;
    }
  }, [navigate, source, sourceTaskId, sourceApprovalId]);
  
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [activeTab, setActiveTab] = useState(null); // Will be set when stages load
  const [showAdvanceModal, setShowAdvanceModal] = useState(false);
  const [advanceComment, setAdvanceComment] = useState('');
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [showingApprovedAdvance, setShowingApprovedAdvance] = useState(false);
  const [showWithdrawApprovalModal, setShowWithdrawApprovalModal] = useState(false);
  const [isWithdrawHover, setIsWithdrawHover] = useState(false);
  const [approvalRequirements, setApprovalRequirements] = useState([]);
  const [requirementsCollapsed, setRequirementsCollapsed] = useState(isMobile ? true : false);
  const [showRevertPanel, setShowRevertPanel] = useState(false);
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closeComment, setCloseComment] = useState('');
  const [childrenToClose, setChildrenToClose] = useState([]);
  const [showCascadeCloseModal, setShowCascadeCloseModal] = useState(false);
  const [isCascadeClose, setIsCascadeClose] = useState(false);
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [openTasksModal, setOpenTasksModal] = useState({ show: false, tasks: [] });
  const [savingFields, setSavingFields] = useState(new Set());
  const [componentValidationResults, setComponentValidationResults] = useState(new Map());
  const [isValidating, setIsValidating] = useState(false);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showRiskMatrixModal, setShowRiskMatrixModal] = useState(false);
  const [showProbabilityModal, setShowProbabilityModal] = useState(false);
  const [deleteRiskSnapshotModal, setDeleteRiskSnapshotModal] = useState({ show: false, snapshot: null });
  
  // Child item locking state
  const [parentInfo, setParentInfo] = useState(null); // { parentId, parentTitle }
  const [isChildItem, setIsChildItem] = useState(false);
  
  // Parent relationship modal state
  const [showParentRelationshipModal, setShowParentRelationshipModal] = useState(false);
  const [pendingParentValue, setPendingParentValue] = useState(null);
  const [pendingParentTitles, setPendingParentTitles] = useState([]);
  const [showOwnerAssignmentModal, setShowOwnerAssignmentModal] = useState(false);
  // { fieldId, value } for a primary/secondary owner change awaiting confirmation
  const [pendingOwnerAssignment, setPendingOwnerAssignment] = useState(null);
  // Owner fields a draft chose to assign silently, replayed on POST at save time
  const [draftOwnerNotifyOptOut, setDraftOwnerNotifyOptOut] = useState([]);
  
  // Approval assignment configuration
  // Specific approvers selected from the assigned role(s) — only these people are
  // notified and allowed to respond. Array of react-select { value, label } options.
  const [selectedApprovers, setSelectedApprovers] = useState([]);
  const [bypassApprovalSelected, setBypassApprovalSelected] = useState(false);

  // Role checks
  const { hasRole: canEdit } = useHasRole('user'); // Can edit if user or higher
  const { hasRole: canEndorse } = useHasRole('endorser');
  const { hasRole: isAdmin } = useHasRole('admin');
  
  // Check if user is ONLY a viewer (has viewer role but not user role)
  const { hasRole: hasViewer } = useHasRole('viewer');
  const isViewerOnly = hasViewer && !canEdit;

  // Fetch workflow stages
  const { data: workflowStages = [] } = useWorkflowStages();
  
  // Fetch role definitions for approval requirements
  const { data: roleDefinitions = {} } = useRoleDefinitions();
  // NOTE: role definitions are still used elsewhere on this page; keep hook.
  // (Roles are not required for owner/secondary-owner based approval requirements.)
  // eslint-disable-next-line no-unused-vars
  const roles = roleDefinitions?.roles || [];
  
  // Get current user for approval requests
  const { data: currentUser } = useCurrentUser();

  // Get current user roles (for role-based approval bypass)
  const { data: userRoles = [] } = useUserRoles();

  const currentUserRoleIds = useMemo(() => {
    return (userRoles || [])
      .map((r) => Number(r.role_id))
      .filter((n) => Number.isFinite(n));
  }, [userRoles]);

  const requiredRoleApprovalIds = useMemo(() => {
    return (approvalRequirements || [])
      .map((req) => Number(req.assigned_to_role))
      .filter((n) => Number.isFinite(n) && n > 0);
  }, [approvalRequirements]);

  const canBypassApprovalByRole = useMemo(() => {
    if (!requiresApproval) return false;
    if (isAdmin) return true;
    if (!requiredRoleApprovalIds.length) return false;
    return requiredRoleApprovalIds.some((id) => currentUserRoleIds.includes(id));
  }, [requiresApproval, isAdmin, requiredRoleApprovalIds, currentUserRoleIds]);
  
  // Fetch all users for user-specific assignments
  const { data: allUsers = [] } = useUsers();
  
  // Fetch all items to check for parent relationships and children
  const { data: allItems = [] } = useItems();
  
  // Create approval mutation
  const createApprovalMutation = useCreateApproval();
  const deleteApprovalMutation = useDeleteApproval();
  
  // Fetch risk matrix configuration for dynamic zone thresholds
  const { data: riskMatrixConfig } = useRiskMatrix();
  
  // Fetch single item (disabled in draft mode — there is nothing to fetch yet)
  const { data: fetchedItem, isLoading: isLoadingItem, error } = useItem(id);

  // The in-progress draft. Shaped like a real item so every `item?.x` reader,
  // field renderer and validation path below works unchanged.
  const [draftItem, setDraftItem] = useState({
    id: null,
    title: '',
    description: '',
    status: 'open',
    approval_status: null,
    current_stage: null,
    category_id: null,
    subcategory_id: null,
    project_id: null,
    owner: null,
    primary_owner: null,
    secondary_owner: null,
    child_of: null,
    created_by: null,
    created_on: null,
    stage_history: []
  });
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  const item = isDraft ? draftItem : fetchedItem;
  const isLoading = isDraft ? false : isLoadingItem;

  const createItemMutation = useCreateItem();

  // A draft always starts at the first stage of the workflow.
  useEffect(() => {
    if (!isDraft || !workflowStages.length) return;
    setDraftItem((prev) => (prev.current_stage ? prev : {
      ...prev,
      current_stage: [...workflowStages].sort(
        (a, b) => (a.stage_order ?? 0) - (b.stage_order ?? 0)
      )[0]?.id ?? null
    }));
  }, [isDraft, workflowStages]);

  // Get direct children of current item - every child, whatever its status.
  // A closed child is still a child; only the closure paths below care about
  // which of them are still open.
  const directChildren = useMemo(() => {
    if (!item?.id || !allItems?.length) return [];
    
    return allItems.filter(childItem => childItem.child_of === item.id);
  }, [item?.id, allItems]);

  // Closing a parent only has to deal with children that are still open.
  const openDirectChildren = useMemo(
    () => directChildren.filter(childItem => childItem.status === 'open'),
    [directChildren]
  );

  // Check if parent is closed (for child items)
  const isParentClosed = useMemo(() => {
    if (!isChildItem || !parentInfo?.parentId) return false;
    const parentItem = allItems?.find(i => i.id === parentInfo.parentId);
    return parentItem?.status === 'closed';
  }, [isChildItem, parentInfo, allItems]);

  // Fetch custom field values for this item
  const { data: customFieldValues = [] } = useCustomFieldValues(id);

  // Fetch custom field definitions for the project (needed to identify cost_impact field)
  const { data: customFields = [] } = useCustomFields(item?.project_id, true);

  // Fetch risk snapshots so non-current stages can show the impact values captured at that stage
  const { data: riskSnapshots = [] } = useRiskSnapshots(item?.id);

  // Check if item is in approval submitted state (locks all editing)
  // Note: approved items should not be considered "awaiting" approval
  
  // Get approval requirements for current stage (after item is loaded)
  const { data: currentStageApprovalRequirements = [] } = useApprovalRequirements(
    item?.current_stage,
    { enabled: !!item?.current_stage }
  );

  // Eligible approvers (users in the assigned role groups) for the current stage,
  // used to populate the "who to notify" multiselect in the approval modal.
  const { data: eligibleApproverGroups = [] } = useEligibleApprovers(
    item?.current_stage,
    { enabled: !!item?.current_stage }
  );

  // Get approved approvals for current item and stage (when approval_status is "approved")
  const { data: approvedApprovals = [] } = useApprovals(
    { 
      item_id: id, 
      stage_id: item?.current_stage, 
      status: 'approved' 
    }, 
    { enabled: !!item?.current_stage && item?.approval_status === 'approved' }
  );
  
  // Get denied approvals for current item and stage (when approval_status is "denied")
  const { data: deniedApprovals = [] } = useApprovals(
    { 
      item_id: id, 
      stage_id: item?.current_stage, 
      status: 'denied' 
    }, 
    { enabled: !!item?.current_stage && item?.approval_status === 'denied' }
  );
  
  // Get pending approvals for current item and stage to properly determine if awaiting approval
  const { data: pendingApprovals = [] } = useApprovals(
    { 
      item_id: id, 
      stage_id: item?.current_stage, 
      status: 'requested' 
    }, 
    { enabled: !!item?.current_stage }
  );
  
  // Update awaiting approval logic to check for actual pending approvals.
  // Lock if EITHER the item is marked submitted OR there is an outstanding pending
  // approval for this item/stage. Using OR (not AND) ensures the item locks even
  // when the current user isn't one of the selected approvers (and therefore may
  // not receive the pending approval row). Withdraw/approve/deny all clear both
  // signals, so this can't get stuck.
  const hasActualPendingApprovals = pendingApprovals.length > 0;
  const isAwaitingApproval =
    item?.approval_status !== 'approved' &&
    item?.approval_status !== 'denied' &&
    (item?.approval_status === 'submitted' || hasActualPendingApprovals);
  const canEditFields = canEdit && !isAwaitingApproval;

  useEffect(() => {
    if (!isAwaitingApproval) {
      setIsWithdrawHover(false);
      setShowWithdrawApprovalModal(false);
    }
  }, [isAwaitingApproval]);
  
  // Fetch tasks for this item
  const { data: itemTasks = [] } = useTasksByParent(id);
  
  // Fetch all tasks for validation (needed for child task validation)
  const { data: allTasks = [] } = useTasks();

  // Simple logic: use the actual current category value
  const currentCategoryId = item?.category_id;

  // Get centralized field configurations
  const { getField, choicesData } = useFieldConfigs({
    currentCategoryId,
    currentProjectId: item?.project_id ?? null,
    onCategoryChange: (selectedOption) => {
      // Handle category change and clear subcategory in a single update
      const categoryValue = selectedOption ? selectedOption.value : null;

      // Draft: change both fields in local state, nothing to PATCH yet.
      if (isDraft) {
        setDraftItem((prev) => ({
          ...prev,
          category_id: categoryValue,
          subcategory_id: null
        }));
        setEditingField(null);
        return;
      }

      // If there's a subcategory to clear, update both fields in one PATCH
      if (item.subcategory_id) {
        updateFieldMutation.mutate({ 
          itemId: id, 
          updateData: { 
            category_id: categoryValue,
            subcategory_id: null 
          },
          fieldId: 'category_id' // For tracking purposes
        });
      } else {
        // Otherwise just update the category
        handleFieldSave('category_id', categoryValue);
      }
    }
  });

  // Create enriched item with custom field values merged in
  const enrichedItem = useMemo(() => {
    if (!item) return null;
    
    // Transform custom field values into the expected format
    const customFieldData = {};
    customFieldValues.forEach(cfv => {
      customFieldData[`custom_field_${cfv.custom_field_id}`] = cfv.value;
    });

    const enriched = { ...item, ...customFieldData };
    return enriched;
  }, [item, customFieldValues]);

  // Map each stage to its most recent snapshot. Snapshots arrive ordered by
  // captured_at ASC, so the last one written for a stage_id wins (the values as
  // they were when the item advanced out of that stage).
  const snapshotsByStage = useMemo(() => {
    const map = {};
    (riskSnapshots || []).forEach((snap) => {
      if (snap?.stage_id != null) {
        map[snap.stage_id] = snap;
      }
    });
    return map;
  }, [riskSnapshots]);

  // Look up a custom field's name (the key used inside snapshot.field_values) by its id
  const customFieldNameById = useMemo(() => {
    const map = {};
    (customFields || []).forEach((cf) => {
      map[cf.id] = cf.name;
    });
    return map;
  }, [customFields]);

  // Custom fields that feed the impact / threat score: the ones flagged
  // "can be used as impact score" (stored as risk_matrix_config.threat_score_fields)
  // plus the probability field that drives the risk matrix. These, together with
  // the calculated risk_zone, are the fields we read from the per-stage snapshot.
  const impactCustomFieldIds = useMemo(() => {
    const ids = new Set();
    // Custom fields flagged "can be used as impact score" (the checkbox)
    (riskMatrixConfig?.threat_score_fields || []).forEach((fid) => {
      const n = Number(fid);
      if (Number.isFinite(n)) ids.add(n);
    });
    // Core impact fields that always drive the risk assessment, regardless of config
    const coreImpactNames = new Set(['probability', 'cost_impact', 'schedule_impact']);
    (customFields || []).forEach((cf) => {
      if (coreImpactNames.has(cf.name)) ids.add(Number(cf.id));
    });
    return ids;
  }, [riskMatrixConfig, customFields]);

  // True for the calculated risk zone and any impact-backed custom field
  const isImpactBackedField = (fieldId) => {
    if (fieldId === 'risk_zone') return true;
    if (typeof fieldId === 'string' && fieldId.startsWith('custom_field_')) {
      const cfId = Number(fieldId.replace('custom_field_', ''));
      return impactCustomFieldIds.has(cfId);
    }
    return false;
  };



  // Fetch stage field mappings for layout
  const { data: stageFieldMappings } = useStageFieldMappings();

  const deleteItemMutation = useDeleteItem({
    onSuccess: () => {
      showToast('Item moved to trash', 'success');
      navigate('/items');
    },
    onError: (error) => {
      showToast('Failed to delete item', 'error');
      console.error('Delete error:', error);
    }
  });

  // Close item mutation
  const closeItemMutation = useUpdateItem({
    onSuccess: () => {
      showToast('Item closed successfully!', 'success');
    },
    onError: (error) => {
      if (error?.status === 409 || error?.details?.code === 'ITEM_CONFLICT') {
        queryClient.invalidateQueries({ queryKey: ['items', id] });
        showToast(error?.message || 'This item was updated by someone else. Please review latest changes and try again.', 'error');
        return;
      }

      if (error?.details?.code === 'ITEM_HAS_ACTIVE_TASKS') {
        const tasks = Array.isArray(error.details.activeTasks) ? error.details.activeTasks : [];
        setOpenTasksModal({ show: true, tasks });
        showToast(error.details.message || 'You must close active tasks before closing this item', 'error');
        console.error('Close blocked by active tasks:', error);
        return;
      }

      showToast('Failed to close item', 'error');
      console.error('Close error:', error);
    }
  });

  // Reopen item mutation
  const reopenItemMutation = useUpdateItem({
    onSuccess: () => {
      showToast('Item reopened successfully!', 'success');
    },
    onError: (error) => {
      if (error?.status === 409 || error?.details?.code === 'ITEM_CONFLICT') {
        queryClient.invalidateQueries({ queryKey: ['items', id] });
        showToast(error?.message || 'This item was updated by someone else. Please review latest changes and try again.', 'error');
        return;
      }

      showToast(error?.message || 'Failed to reopen item', 'error');
      console.error('Reopen error:', error);
    }
  });

  // Individual field update mutation
  const updateFieldMutation = useUpdateItem({
    onMutate: ({ fieldId }) => {
      setSavingFields(prev => new Set([...prev, fieldId]));
    },
    onSuccess: (data, { fieldId }) => {
      //console.log('Field update success - fieldId:', fieldId, 'response data:', data);
      setSavingFields(prev => {
        const newSet = new Set(prev);
        newSet.delete(fieldId);
        return newSet;
      });
      
      // Close editing field after successful save
      setEditingField(null);
      
      // Invalidate the specific item query to refresh its data
      queryClient.invalidateQueries({ queryKey: ['items', id] });
      
      // Invalidate items list queries to refresh the lists when returning to items page
      queryClient.invalidateQueries({ queryKey: ['items'] }); // This invalidates all items queries
    },
    onError: (error, { fieldId }) => {
      setSavingFields(prev => {
        const newSet = new Set(prev);
        newSet.delete(fieldId);
        return newSet;
      });

      if (error?.status === 409 || error?.details?.code === 'ITEM_CONFLICT') {
        queryClient.invalidateQueries({ queryKey: ['items', id] });
        setToast({
          show: true,
          message: error?.message || 'This item was updated by someone else. Please review latest changes and try again.',
          type: 'error'
        });
        return;
      }

      setToast({
        show: true,
        message: `Failed to update ${fieldId}: ${error.message}`,
        type: 'error'
      });
    }
  });

  // Custom field update mutation
  const updateCustomFieldMutation = useUpdateCustomFieldValue({
    onMutate: ({ fieldId }) => {
      setSavingFields(prev => new Set([...prev, `custom_field_${fieldId}`]));
    },
    onSuccess: (data, { fieldId }) => {
      setSavingFields(prev => {
        const newSet = new Set(prev);
        newSet.delete(`custom_field_${fieldId}`);
        return newSet;
      });
      
      // Close editing field after successful save - ensure it clears the custom field editing state
      if (editingField === `custom_field_${fieldId}` || editingField?.startsWith('custom_field_')) {
        setEditingField(null);
      }
    },
    onError: (error, { fieldId }) => {
      setSavingFields(prev => {
        const newSet = new Set(prev);
        newSet.delete(`custom_field_${fieldId}`);
        return newSet;
      });

      if (error?.status === 409 || error?.details?.code === 'ITEM_CONFLICT') {
        queryClient.invalidateQueries({ queryKey: ['items', id] });
        setToast({
          show: true,
          message: error?.message || 'This item was updated by someone else. Please review latest changes and try again.',
          type: 'error'
        });
        return;
      }

      setToast({
        show: true,
        message: `Failed to update custom field: ${error.message}`,
        type: 'error'
      });
    }
  });

  // Advance stage mutation
  const advanceStageeMutation = useAdvanceItemStage({
    onSuccess: (data, { nextStageId }) => {
      const nextStage = workflowStages.find(s => s.id === nextStageId);
      showToast(`Advanced to ${nextStage?.stage_name || 'next stage'}!`, 'success');
      setActiveTab(nextStageId);
    },
    onError: (error) => {
      if (error?.status === 409 || error?.details?.code === 'ITEM_CONFLICT') {
        queryClient.invalidateQueries({ queryKey: ['items', id] });
        showToast(error?.message || 'This item was updated by someone else. Please review latest changes and try again.', 'error');
        return;
      }

      showToast(error?.message || 'Failed to advance stage', 'error');
      console.error('Advance stage error:', error);
    }
  });

  // Revert stage mutation
  const revertStageeMutation = useRevertItemStage({
    onSuccess: (data, { previousStageId }) => {
      const previousStage = workflowStages.find(s => s.id === previousStageId);
      showToast(`Reverted to ${previousStage?.stage_name || 'previous stage'}!`, 'success');
      setActiveTab(previousStageId);
      setShowRevertModal(false);
    },
    onError: (error) => {
      if (error?.status === 409 || error?.details?.code === 'ITEM_CONFLICT') {
        queryClient.invalidateQueries({ queryKey: ['items', id] });
        showToast(error?.message || 'This item was updated by someone else. Please review latest changes and try again.', 'error');
        return;
      }

      showToast(error?.message || 'Failed to revert stage', 'error');
      console.error('Revert stage error:', error);
    }
  });

  // Delete risk snapshot mutation
  const deleteRiskSnapshotMutation = useDeleteRiskSnapshot({
    onSuccess: () => {
      setDeleteRiskSnapshotModal({ show: false, snapshot: null });
      showToast('Risk snapshot deleted successfully', 'success');
    },
    onError: (error) => {
      console.error('Error deleting risk snapshot:', error);
      showToast('Failed to delete risk snapshot', 'error');
    }
  });

  // Initialize `activeTab` from the URL (or item stage) ONCE.
  // After initialization, `activeTab` is the source of truth; we still write it to the URL for
  // shareable links, but we do not continuously re-read the URL to avoid feedback loops.
  useEffect(() => {
    if (workflowStages.length === 0 || !item) return;

    const urlTab = searchParams.get('tab');

    setActiveTab((prev) => {
      if (prev !== null && prev !== undefined) return prev;

      const hashTab = window.location.hash?.substring(1) || null;
      const candidate = urlTab || hashTab;

      const specialTabs = new Set(['documents', 'risk-history']);
      const stageIdSet = new Set(workflowStages.map(s => String(s.id)));

      if (candidate && specialTabs.has(candidate)) {
        return candidate;
      }

      if (candidate && stageIdSet.has(String(candidate))) {
        return parseInt(candidate, 10);
      }

      if (item.current_stage) return item.current_stage;
      return workflowStages[0].id;
    });
  }, [workflowStages, item, searchParams]);

  // Keep the current tab in the URL so refresh + shareable links land on the same tab
  useEffect(() => {
    if (activeTab === null || activeTab === undefined) return;

    const tabParam = typeof activeTab === 'number' ? String(activeTab) : activeTab;
    if (!tabParam) return;
    if (searchParams.get('tab') === tabParam) return;

    const next = new URLSearchParams(searchParams);
    next.set('tab', tabParam);
    setSearchParams(next, { replace: true });
  }, [activeTab, searchParams, setSearchParams]);

  // Check if current item is a child of another item
  useEffect(() => {
    if (!item?.id) {
      setIsChildItem(false);
      setParentInfo(null);
      return;
    }

    // Check if current item has a parent (single child_of value)
    if (item.child_of) {
      // This item IS a child - find the parent item
      const parentId = item.child_of;
      const parentItem = allItems?.find(otherItem => otherItem.id === parentId);
      
      setIsChildItem(true);
      if (parentItem) {
        setParentInfo({
          parentId: parentItem.id,
          parentTitle: parentItem.title
        });
      } else {
        // Parent not found, might be deleted or not loaded
        setParentInfo({
          parentId: parentId,
          parentTitle: `Unknown Item ${formatItemRef(parentId)}`
        });
      }
    } else {
      // This item is NOT a child (it's a parent or standalone item)
      setIsChildItem(false);
      setParentInfo(null);
    }
  }, [item, allItems]);

  // Handle clicks outside of editing field to save and exit edit mode
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside actions menu
      if (showActionsMenu) {
        const clickedElement = event.target;
        const actionsDropdown = clickedElement.closest('.actions-dropdown');
        if (!actionsDropdown) {
          setShowActionsMenu(false);
        }
      }

      if (editingField) {
        // Check if the click is on an input field or select component that's currently being edited
        const clickedElement = event.target;
        const isInputField = clickedElement.tagName === 'INPUT' || 
                           clickedElement.tagName === 'TEXTAREA' ||
                           clickedElement.closest('.inline-edit-field') ||
                           clickedElement.closest('.inline-edit-select') ||
                           clickedElement.closest('[class*="react-select"]') ||
                           clickedElement.closest('[class*="select__"]');
        
        if (!isInputField) {
          // Find the currently focused input field and trigger its blur event to save changes
          const currentInput = document.querySelector('.inline-edit-field:focus, .inline-edit-field');
          if (currentInput) {
            currentInput.blur(); // This will trigger the handleBlur function which handles saving
          } else {
            // Fallback: just exit edit mode if no input found
            setEditingField(null);
          }
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editingField, showActionsMenu]);

  // Simple function to validate component fields when needed
  const validateComponentFieldsForStage = useCallback(async (stageId) => {
    // A draft cannot advance a stage, and these validators query by item id —
    // which is null here — so there is nothing useful to run.
    if (isDraft) return;

    if (!stageFieldMappings || !item || isValidating) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`⏸️ Skipping validation for stage ${stageId} - missing data or already validating`);
      }
      return;
    }
    
    setIsValidating(true);
    
    try {
      const stageMappings = stageFieldMappings.mappings?.filter(m => m.stage_id === stageId && m.required) || [];
      const visibleMappings = stageMappings.filter(mapping => evaluateFieldVisibility(mapping, enrichedItem));
      
      const componentMappings = visibleMappings.filter(mapping => {
        const config = getField(mapping.field_id);
        return config.type === 'component' && config.validation?.validateRequired;
      });

      if (componentMappings.length > 0) {
        
        // Invalidate tasks query to get fresh data before validation
        const hasTasksValidation = componentMappings.some(m => m.field_id === 'tasks');
        if (hasTasksValidation) {
          await queryClient.invalidateQueries({ queryKey: ['tasks', 'by-parent', id] });
          // Small delay to ensure fresh data is loaded
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        const componentValidations = componentMappings.map(async (mapping) => {
          try {
            const config = getField(mapping.field_id);
            
            // Handle special validation for tasks field - always use fresh data
            if (mapping.field_id === 'tasks') {
              const result = await config.validation.validateRequired(allTasks, item.id, allItems);
              return {
                fieldId: mapping.field_id,
                ...result
              };
            } else {
              const result = await config.validation.validateRequired(item.id);
              return {
                fieldId: mapping.field_id,
                ...result
              };
            }
          } catch (error) {
            console.error(`Error validating component field ${mapping.field_id}:`, error);
            return {
              fieldId: mapping.field_id,
              isValid: false,
              message: 'Validation error occurred'
            };
          }
        });

        const results = await Promise.all(componentValidations);
        const validationMap = new Map();
        results.forEach(result => {
          validationMap.set(result.fieldId, result);
        });
        setComponentValidationResults(validationMap);
      }
    } catch (error) {
      console.error('Error during component validation:', error);
    } finally {
      setIsValidating(false);
    }
  }, [isDraft, stageFieldMappings, item, isValidating, enrichedItem, getField, queryClient, id, allTasks, allItems]);

  // Re-validate whenever stage changes (but not when data changes to avoid infinite loop)
  useEffect(() => {
    if (activeTab && stageFieldMappings && item && !isValidating) {
      const stageMappings = stageFieldMappings.mappings?.filter(m => m.stage_id === activeTab && m.required) || [];
      const visibleMappings = stageMappings.filter(mapping => evaluateFieldVisibility(mapping, enrichedItem));
      const hasComponentFields = visibleMappings.some(mapping => {
        const config = getField(mapping.field_id);
        return config?.type === 'component' && config.validation?.validateRequired;
      });
      
      if (hasComponentFields) {
        //console.log(`🔄 Stage changed, validating stage ${activeTab}`);
        // Clear previous validation results to ensure fresh validation
        setComponentValidationResults(new Map());
        validateComponentFieldsForStage(activeTab);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, stageFieldMappings?.mappings?.length]);

  // Handle tab click with validation trigger
  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    
    // Only validate if clicking on a stage tab (not documents or other special tabs)
    if (typeof tabId === 'number' && stageFieldMappings && item && !isValidating) {
      const stageMappings = stageFieldMappings.mappings?.filter(m => m.stage_id === tabId && m.required) || [];
      const visibleMappings = stageMappings.filter(mapping => evaluateFieldVisibility(mapping, enrichedItem));
      const hasComponentFields = visibleMappings.some(mapping => {
        const config = getField(mapping.field_id);
        return config?.type === 'component' && config.validation?.validateRequired;
      });
      
      if (hasComponentFields) {
        //console.log(`🔄 Triggering validation for tab ${tabId} (has component fields)`);
        // Clear previous validation results and validate with fresh data
        setComponentValidationResults(new Map());
        setTimeout(() => {
          validateComponentFieldsForStage(tabId);
        }, 10);
      }
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const handleCloseToast = () => {
    setToast({ show: false, message: '', type: 'success' });
  };

  // Check if next stage requires approval (now using hook data)
  const checkApprovalRequirements = (currentStageId) => {
    if (!workflowStages.length || !currentStageId || !currentStageApprovalRequirements) {
      return { requiresApproval: false, requirements: [] };
    }
    
    if (currentStageApprovalRequirements.length > 0) {
      return { requiresApproval: true, requirements: currentStageApprovalRequirements };
    }
    
    return { requiresApproval: false, requirements: [] };
  };

  const normalizeEmail = (value) => String(value || '').trim().toLowerCase();

  const hasItemOwnerRequirement = useMemo(() => {
    return (approvalRequirements || []).some((req) => Number(req.assigned_to_role) === -1);
  }, [approvalRequirements]);

  const hasItemSecondaryOwnerRequirement = useMemo(() => {
    return (approvalRequirements || []).some((req) => Number(req.assigned_to_role) === -2);
  }, [approvalRequirements]);

  const primaryOwnerDisplay = useMemo(() => {
    const email = item?.primary_owner;
    if (!email) return '';
    const match = allUsers?.find((u) => normalizeEmail(u.email) === normalizeEmail(email));
    return match ? `${match.name} (${match.email})` : String(email);
  }, [item?.primary_owner, allUsers]);

  const secondaryOwnerDisplay = useMemo(() => {
    const email = item?.secondary_owner;
    if (!email) return '';
    const match = allUsers?.find((u) => normalizeEmail(u.email) === normalizeEmail(email));
    return match ? `${match.name} (${match.email})` : String(email);
  }, [item?.secondary_owner, allUsers]);

  // Display name/email for the owner change sitting in the confirmation modal
  const pendingOwnerDisplay = useMemo(() => {
    const email = pendingOwnerAssignment?.value;
    if (!email) return '';
    const match = allUsers?.find((u) => normalizeEmail(u.email) === normalizeEmail(email));
    return match ? `${match.name} (${match.email})` : String(email);
  }, [pendingOwnerAssignment, allUsers]);

  const pendingOwnerRoleLabel =
    pendingOwnerAssignment?.fieldId === 'secondary_owner' ? 'Secondary Owner' : 'Primary Owner';

  const pendingOwnerReplaces = useMemo(() => {
    const fieldId = pendingOwnerAssignment?.fieldId;
    if (!fieldId) return '';
    const email = item?.[fieldId];
    if (!email) return '';
    const match = allUsers?.find((u) => normalizeEmail(u.email) === normalizeEmail(email));
    return match ? `${match.name} (${match.email})` : String(email);
  }, [pendingOwnerAssignment, item, allUsers]);

  const missingRequiredOwners = useMemo(() => {
    const missing = [];
    if (hasItemOwnerRequirement && !normalizeEmail(item?.primary_owner)) {
      missing.push('Item Owner');
    }
    if (hasItemSecondaryOwnerRequirement && !normalizeEmail(item?.secondary_owner)) {
      missing.push('Item Secondary Owner');
    }
    return missing;
  }, [hasItemOwnerRequirement, hasItemSecondaryOwnerRequirement, item?.primary_owner, item?.secondary_owner]);

  // Whether the current stage has at least one real role requirement (i.e. a group
  // the requester must pick specific approvers from, excluding item-owner requirements).
  const hasRealRoleRequirement = useMemo(() => {
    return (approvalRequirements || []).some((req) => Number(req.assigned_to_role) > 0);
  }, [approvalRequirements]);

  // Grouped react-select options for the "who to notify" approver multiselect.
  const approverOptions = useMemo(() => {
    return (eligibleApproverGroups || [])
      .filter((group) => Array.isArray(group.users) && group.users.length > 0)
      .map((group) => ({
        label: group.role_name,
        options: group.users.map((u) => ({
          value: u.id,
          label: `${u.name} (${u.email})`
        }))
      }));
  }, [eligibleApproverGroups]);

  const handleAdvanceStage = () => {
    if (!workflowStages.length) return;
    
    // If approval is required, create approval request instead of advancing
    if (requiresApproval && approvalRequirements.length > 0) {
      if (bypassApprovalSelected && canBypassApprovalByRole) {
        // Allow privileged users (by role) to advance without creating an approval.
        // Server enforces authorization via bypass_approval.
      } else {
        handleRequestApproval();
        return;
      }
    }
    
    // Normal stage advancement logic (no approval required)
    // Prepare advance comment data
    const currentStageName = workflowStages.find(s => s.id === item.current_stage)?.stage_name || 'Unknown';
    const advanceCommentEntry = {
      stage: currentStageName,
      comment: advanceComment || '',
      timestamp: new Date().toISOString(),
      user: item.created_by || 'system' // You might want to get the current user from auth context
    };
    
    // If no current stage, start with first stage
    if (!item.current_stage) {
      const nextStageId = workflowStages[0].id;
      const stageData = {
        title: item.title || '',
        description: item.description || '',
        approval_status: (bypassApprovalSelected && canBypassApprovalByRole) ? null : (showingApprovedAdvance ? null : (item.approval_status || null)), // Clear approval status when bypassing/approved
        owner: item.owner || '',
        created_by: item.created_by || '',
        category_id: item.category_id || null,
        subcategory_id: item.subcategory_id || null,
        project_id: item.project_id || null,
        current_stage: nextStageId,
        advance_comments: [advanceCommentEntry],
        ...(bypassApprovalSelected && canBypassApprovalByRole ? { bypass_approval: true } : {})
      };
      advanceStageeMutation.mutate({ itemId: id, stageData, nextStageId });
      return;
    }
    
    const currentStageIndex = workflowStages.findIndex(s => s.id === item.current_stage);
    if (currentStageIndex < 0 || currentStageIndex >= workflowStages.length - 1) return;
    
    const nextStage = workflowStages[currentStageIndex + 1];
    
    // Append to existing advance comments
    const existingComments = item.advance_comments || [];
    const updatedComments = [...existingComments, advanceCommentEntry];
    
    const stageData = {
      title: item.title || '',
      description: item.description || '',
      approval_status: (bypassApprovalSelected && canBypassApprovalByRole) ? null : (showingApprovedAdvance ? null : (item.approval_status || null)), // Clear approval status when bypassing/approved
      owner: item.owner || '',
      created_by: item.created_by || '',
      category_id: item.category_id || null,
      subcategory_id: item.subcategory_id || null,
      project_id: item.project_id || null,
      current_stage: nextStage.id,
      advance_comments: updatedComments,
      ...(bypassApprovalSelected && canBypassApprovalByRole ? { bypass_approval: true } : {})
    };
    advanceStageeMutation.mutate({ itemId: id, stageData, nextStageId: nextStage.id });
  };

  // Handle approval request creation
  const handleRequestApproval = () => {
    if (!currentUser?.id) {
      setToast({ 
        show: true, 
        message: 'Error: Current user not found', 
        type: 'error' 
      });
      return;
    }

    // If resubmitting (item was previously denied), we need to handle existing approvals
    const isResubmission = item?.approval_status === 'denied';

    // Update item approval_status to "submitted" first
    updateFieldMutation.mutate({ 
      itemId: id, 
      updateData: { approval_status: 'submitted' },
      fieldId: 'approval_status'
    }, {
      onSuccess: () => {
        // Role-based assignment restricted to the specifically selected approvers.
        const roleIds = (approvalRequirements || [])
          .map((req) => Number(req.assigned_to_role))
          .filter((n) => Number.isFinite(n));

        // Deduplicate selected approver IDs (a user can appear under multiple roles).
        const selectedUserIds = Array.from(
          new Set((selectedApprovers || []).map((a) => a.value))
        );

        const approvalData = {
          item_id: parseInt(id),
          stage_id: item.current_stage,
          assigned_to_roles: roleIds, // Send array of role IDs
          assigned_to_user_ids: selectedUserIds, // Specific people to notify / who can respond
          assignment_type: 'role',
          requested_by: currentUser.id,
          comments: advanceComment || `${isResubmission ? 'Resubmitted approval' : 'Approval'} request for advancing from ${workflowStages.find(s => s.id === item.current_stage)?.stage_name}`,
          is_resubmission: isResubmission
        };

        if (approvalData.assigned_to_roles?.length > 0) {
          // Show who was notified: selected people, else the role names (owner-only case).
          const assignmentDisplay = selectedApprovers.length > 0
            ? selectedApprovers.map((a) => a.label).join(', ')
            : (approvalRequirements || []).map((req) => req.role_name).join(', ');

          createApprovalMutation.mutate(approvalData, {
            onSuccess: () => {
              setToast({ 
                show: true, 
                message: `${isResubmission ? 'Resubmitted approval' : 'Approval'} request sent to ${assignmentDisplay}`, 
                type: 'success' 
              });
            },
            onError: (error) => {
              setToast({ 
                show: true, 
                message: `Error ${isResubmission ? 'resubmitting' : 'requesting'} approval: ${error.message}`, 
                type: 'error' 
              });
            }
          });
        } else {
          setToast({ 
            show: true, 
            message: 'Error: No valid assignment target selected', 
            type: 'error' 
          });
        }
      },
      onError: (error) => {
        setToast({ 
          show: true, 
          message: `Error updating item status: ${error.message}`, 
          type: 'error' 
        });
      }
    });
    
    setShowAdvanceModal(false);
    setAdvanceComment('');
    setRequiresApproval(false);
    setApprovalRequirements([]);
    setSelectedApprovers([]);
  };





  // Helper function to evaluate if a field should be visible based on visibility conditions
  const evaluateFieldVisibility = (fieldMapping, itemValues) => {
    // If no visibility conditions, field is always visible (backward compatibility)
    if (!fieldMapping.visibility_conditions) {
      return true;
    }

    const { field_id, operator, value } = fieldMapping.visibility_conditions;
    
    // If visibility condition is incomplete, treat as always visible
    if (!field_id || !operator || value === undefined || value === null) {
      return true;
    }

    // Get the controlling field's current value
    const controllingFieldValue = itemValues[field_id];
    
    // If controlling field is missing/null/undefined, condition is not met
    if (controllingFieldValue === null || controllingFieldValue === undefined) {
      return false;
    }

    // Apply operator logic with loose equality for string/number coercion
    switch (operator) {
      case 'equals':
        return controllingFieldValue == value; // eslint-disable-line eqeqeq
      case 'not_equals':
        return controllingFieldValue != value; // eslint-disable-line eqeqeq
      case 'has_value':
        // For has_value, we check if field has any non-empty value
        return controllingFieldValue !== null && 
               controllingFieldValue !== undefined && 
               controllingFieldValue !== '' && 
               (Array.isArray(controllingFieldValue) ? controllingFieldValue.length > 0 : true);
      case 'does_not_have_value':
        // For does_not_have_value, we check if field is empty/null/undefined
        return controllingFieldValue === null || 
               controllingFieldValue === undefined || 
               controllingFieldValue === '' || 
               (Array.isArray(controllingFieldValue) && controllingFieldValue.length === 0);
      default:
        console.warn(`Unknown visibility operator: ${operator}`);
        return true; // Default to visible for unknown operators
    }
  };

  // Helper function to organize fields by stage and subsection
  const getStageFieldStructure = (stageId) => {
    if (!stageFieldMappings) return { subsections: [], fieldsWithoutSubsection: [] };

    // Get mappings and subsections for this stage
    const stageMappings = stageFieldMappings.mappings?.filter(m => m.stage_id === stageId) || [];
    // Filter to only visible fields
    const visibleMappings = stageMappings.filter(mapping => evaluateFieldVisibility(mapping, enrichedItem));
    const stageSubsections = stageFieldMappings.subsections?.filter(s => s.stage_id === stageId) || [];

    // Sort subsections by display order
    const sortedSubsections = stageSubsections.sort((a, b) => a.display_order - b.display_order);

    // Organize fields by subsection
    const subsectionsWithFields = sortedSubsections.map(subsection => {
      const subsectionFields = visibleMappings
        .filter(m => m.subsection_id === subsection.id)
        .sort((a, b) => a.display_order - b.display_order);
      
      return {
        ...subsection,
        fields: subsectionFields
      };
    });

    // Get fields without subsection
    const fieldsWithoutSubsection = visibleMappings
      .filter(m => !m.subsection_id)
      .sort((a, b) => a.display_order - b.display_order);

    return {
      subsections: subsectionsWithFields,
      fieldsWithoutSubsection
    };
  };

  // Helper function to get stage layout (configurable per stage)
  const getStageLayout = (stageId) => {
    // Load stage layouts from localStorage
    try {
      const savedLayouts = localStorage.getItem('stageLayouts');
      if (savedLayouts) {
        const parsedLayouts = JSON.parse(savedLayouts);
        return parsedLayouts[stageId] || 2; // Default to 2 columns
      }
    } catch (error) {
      console.error('Error loading stage layouts:', error);
    }
    return 2; // Default 2-column layout
  };

  // Performs the actual write for a field. Split out of handleFieldSave so the
  // confirmation modals can run the same save once the user confirms.
  // `notify: false` saves an owner assignment without emailing the new owner.
  const commitFieldSave = (fieldId, value, { notify = true } = {}) => {
    // Process the value based on field type
    let processedValue = value;
    const fieldConfig = getField(fieldId);

    // For numeric fields, convert empty strings to null
    if (fieldConfig && fieldConfig.type === 'number') {
      if (value === '' || value === null || value === undefined) {
        processedValue = null;
      } else {
        // Ensure it's a valid number
        const numValue = parseFloat(value);
        processedValue = isNaN(numValue) ? null : numValue;
      }
    }

    const isOwnerField = OWNER_FIELD_IDS.includes(fieldId);

    // A draft has no row to update - buffer the edit until Save.
    if (isDraft) {
      setDraftItem((prev) => ({ ...prev, [fieldId]: processedValue }));
      // Remember the notify choice so the create call can replay it. A later edit
      // of the same field replaces the earlier choice rather than stacking on it.
      if (isOwnerField) {
        setDraftOwnerNotifyOptOut((prev) => {
          const rest = prev.filter((f) => f !== fieldId);
          return notify ? rest : [...rest, fieldId];
        });
      }
      setEditingField(null);
      return;
    }

    // Check if this is a custom field
    if (fieldId.startsWith('custom_field_')) {
      const numericFieldId = fieldId.replace('custom_field_', '');

      updateCustomFieldMutation.mutate({
        itemId: id,
        fieldId: numericFieldId,
        value: processedValue
      });

      // For custom fields, immediately clear editing state since the mutation is async
      // The success callback will also clear it, but this ensures immediate UI response
      setEditingField(null);
    } else {
      const updateData = { [fieldId]: processedValue };
      if (isOwnerField && !notify) {
        // Request-only flag; the server strips it before building the UPDATE.
        updateData.suppress_owner_notification = [fieldId];
      }

      updateFieldMutation.mutate({
        itemId: id,
        updateData,
        fieldId // Pass fieldId for the callbacks
      });
    }
  };

  // Helper function to handle field save
  const handleFieldSave = (fieldId, value) => {
    // Special handling for child_of field - show confirmation modal
    if (fieldId === 'child_of' && value) {
      // Check if this item wasn't already a child (establishing new parent relationship)
      const wasChild = !!item?.child_of;
      const isNewParent = !wasChild || (wasChild && value !== item.child_of);
      
      if (isNewParent) {
        // Get parent title for the modal
        const parent = allItems.find(i => i.id === value);
        const parentTitle = parent
          ? `${formatItemRef(parent.id)} - ${parent.title}`
          : formatItemRef(value);
        
        setPendingParentValue(value);
        setPendingParentTitles([parentTitle]); // Keep as array for modal compatibility
        setShowParentRelationshipModal(true);
        return; // Don't save immediately, wait for modal confirmation
      }
    }
    
    // Prevent duplicate mutations if this field is already being saved
    if (savingFields.has(fieldId)) {
      console.log('Skipping duplicate mutation for field:', fieldId);
      return;
    }
    
    // Validation for secondary owner field
    if (fieldId === 'secondary_owner' && value && value === item?.primary_owner) {
      showToast('Secondary owner cannot be the same as primary owner', 'error');
      return;
    }

    // Validation for primary owner field (symmetric)
    if (fieldId === 'primary_owner' && value && value === item?.secondary_owner) {
      showToast('Primary owner cannot be the same as secondary owner', 'error');
      return;
    }

    // Assigning an owner emails that person, so confirm before saving. Clearing an
    // owner, or re-picking the one already set, sends nothing and needs no prompt.
    const isOwnerAssignment =
      OWNER_FIELD_IDS.includes(fieldId) &&
      !!value &&
      normalizeEmail(value) !== normalizeEmail(item?.[fieldId]);

    if (isOwnerAssignment) {
      setPendingOwnerAssignment({ fieldId, value });
      setShowOwnerAssignmentModal(true);
      return; // Wait for modal confirmation before saving
    }

    commitFieldSave(fieldId, value);
  };

  // Handlers for the owner assignment confirmation modal. Both confirm buttons make
  // the same assignment; `notify` only decides whether the new owner is emailed.
  const handleConfirmOwnerAssignment = (notify) => {
    if (!pendingOwnerAssignment) return;

    const { fieldId, value } = pendingOwnerAssignment;

    setShowOwnerAssignmentModal(false);
    setPendingOwnerAssignment(null);

    if (savingFields.has(fieldId)) {
      console.log('Skipping duplicate mutation for field:', fieldId);
      setEditingField(null);
      return;
    }

    commitFieldSave(fieldId, value, { notify });
  };

  const handleCancelOwnerAssignment = () => {
    setShowOwnerAssignmentModal(false);
    setPendingOwnerAssignment(null);
    setEditingField(null);
  };
  
  // Handler for parent relationship modal
  const handleConfirmParentRelationship = () => {
    const fieldId = 'child_of';
    const value = pendingParentValue;
    
    // Proceed with the actual save
    if (savingFields.has(fieldId)) {
      console.log('Skipping duplicate mutation for field:', fieldId);
      return;
    }
    
    // Process the value and save
    updateFieldMutation.mutate({ 
      itemId: id, 
      updateData: { [fieldId]: value },
      fieldId
    });
    
    // Close modal and reset state
    setShowParentRelationshipModal(false);
    setPendingParentValue(null);
    setPendingParentTitles([]);
    setEditingField(null);
  };
  
  const handleCancelParentRelationship = () => {
    setShowParentRelationshipModal(false);
    setPendingParentValue(null);
    setPendingParentTitles([]);
    setEditingField(null);
  };

  // A draft is only worth keeping once it has a title, so that is what Save requires.
  const draftTitle = (draftItem.title || '').trim();
  // canEdit is belt-and-braces: the /items/new route is already role-gated, and
  // POST /api/items rejects anyone below 'user'.
  const canSaveDraft = isDraft && canEdit && draftTitle.length > 0 && !isSavingDraft;

  const handleSaveDraft = async () => {
    if (!canSaveDraft) {
      if (!draftTitle) showToast('A title is required before saving.', 'error');
      return;
    }

    setIsSavingDraft(true);
    try {
      // Only opt out for owners the draft actually still carries.
      const suppressOwnerNotification = draftOwnerNotifyOptOut.filter(
        (field) => draftItem[field]
      );

      const created = await createItemMutation.mutateAsync({
        ...Object.fromEntries(
          DRAFT_PERSISTED_FIELDS
            .map((field) => [field, draftItem[field] ?? null])
            .filter(([, value]) => value !== null && value !== '')
        ),
        title: draftTitle,
        ...(suppressOwnerNotification.length > 0
          ? { suppress_owner_notification: suppressOwnerNotification }
          : {})
      });

      // Replace the draft route so Back does not land the user on an empty draft.
      navigate(`/items/${created.id}`, { replace: true });
    } catch (err) {
      console.error('Error creating item:', err);
      showToast(err?.message || 'Failed to create item', 'error');
      setIsSavingDraft(false);
    }
  };

  const handleDiscardDraft = () => {
    navigate('/items');
  };

  // Helper function to check if all required fields for risk zone calculation are present
  const checkRiskZoneRequiredFields = () => {
    const config = getField('risk_zone');
    if (!config || !config.dependentFields) return { canCalculate: true, missingFields: [] };
    
    const missingFields = [];
    
    for (const depFieldId of config.dependentFields) {
      const fieldValue = enrichedItem[depFieldId];
      const fieldConfig = getField(depFieldId);
      
      // Check if the field has a value
      if (!fieldValue || fieldValue === '' || fieldValue === null || fieldValue === undefined) {
        missingFields.push(fieldConfig?.label || depFieldId);
      }
    }
    
    return {
      canCalculate: missingFields.length === 0,
      missingFields: missingFields
    };
  };

  // The probability custom field drives the risk matrix Y-axis; its buckets are shown in a modal
  const probabilityField = useMemo(
    () => (customFields || []).find((cf) => cf && cf.name === 'probability') || null,
    [customFields]
  );
  const probabilityFieldId = probabilityField ? `custom_field_${probabilityField.id}` : null;

  // Info button that opens the read-only probability scale (mirrors the cost impact scale button)
  const renderProbabilityInfoButton = () => (
    <button
      type="button"
      className="probability-info-btn"
      onClick={(e) => {
        e.preventDefault();
        setShowProbabilityModal(true);
      }}
      title="View probability scale"
    >
      <i className="bi bi-info-square"></i>
    </button>
  );

  // Info button that opens the read-only risk matrix (mirrors the cost impact scale button)
  const renderRiskMatrixInfoButton = () => (
    <button
      type="button"
      className="risk-zone-info-btn"
      onClick={(e) => {
        e.preventDefault();
        setShowRiskMatrixModal(true);
      }}
      title="View risk matrix"
    >
      <i className="bi bi-info-square"></i>
    </button>
  );

  // Read-only label shared by the historical (non-current stage) renderers below
  const renderHistoricalFieldLabel = (fieldId, config, isRequired) => (
    <label>
      {config.tooltip ? (
        <div className="tooltip-container">
          <span>{config.label}</span>
          <span className="tooltip-icon">i</span>
          <div className="tooltip-content">{config.tooltip}</div>
        </div>
      ) : (
        config.label
      )}
      {isRequired && <span className="required-indicator">*</span>}
      {fieldId === 'risk_zone' && renderRiskMatrixInfoButton()}
      {probabilityFieldId && fieldId === probabilityFieldId && renderProbabilityInfoButton()}
    </label>
  );

  // Render an impact field for a non-current stage using the value captured in
  // that stage's snapshot, rather than the live (locked, greyed-out) database value.
  const renderHistoricalImpactField = (fieldId, config, snapshotValue, isRequired) => (
    <div key={fieldId} className="detail-field">
      {renderHistoricalFieldLabel(fieldId, config, isRequired)}
      <div className="field-value-container stage-locked historical-snapshot-value">
        <div className="field-value">
          {formatDisplayValue(fieldId, snapshotValue, config, { navigate, item, choicesData, riskMatrixConfig })}
          <span className="stage-lock-indicator" title="Value recorded in this stage's risk snapshot">
            <i className="bi bi-clock-history"></i>
          </span>
        </div>
      </div>
    </div>
  );

  // Render an impact field for a stage that has no snapshot (e.g. a stage the
  // item has not reached yet, or one that predates snapshotting).
  const renderNoSnapshotField = (fieldId, config, isRequired) => (
    <div key={fieldId} className="detail-field">
      {renderHistoricalFieldLabel(fieldId, config, isRequired)}
      <div className="field-value-container stage-locked historical-snapshot-missing">
        <div className="field-value no-snapshot-message">
          <i className="bi bi-exclamation-circle"></i>
          <span>No risk snapshot available</span>
        </div>
      </div>
    </div>
  );

  // Helper function to render a field based on its type
  const renderField = (fieldMapping, stageId) => {
    const fieldId = fieldMapping.field_id;
    const isRequired = fieldMapping.required;
    const isStageReadonly = fieldMapping.readonly || false; // Add stage-specific readonly check
    const fieldValue = enrichedItem[fieldId];
    const isCurrentlyEditing = editingField === fieldId;
    const isSaving = savingFields.has(fieldId);

    // In a draft, only fields the create call actually persists may be edited.
    // Custom fields live in their own item_id-keyed table and calculated values
    // are derived server-side, so both are locked here rather than accepting an
    // edit that Save would silently drop.
    if (isDraft && fieldId && !DRAFT_PERSISTED_FIELDS.includes(fieldId)) {
      const config = getField(fieldId);
      return (
        <div key={fieldId} className="detail-field">
          <label>
            {config?.label || fieldId}
            {isRequired && <span className="required-indicator">*</span>}
          </label>
          <div className="draft-locked-panel">
            <i className="bi bi-lock-fill"></i>
            Available after the item is saved.
          </div>
        </div>
      );
    }

    // Impact fields and the risk zone on a NON-current stage must reflect what was
    // captured in that stage's snapshot, so users see the assessment they entered at
    // that stage instead of the current (locked) values. The current stage keeps its
    // live, editable values.
    if (isImpactBackedField(fieldId) && item?.current_stage !== stageId) {
      const config = getField(fieldId);
      const snapshot = snapshotsByStage[stageId];

      if (!snapshot) {
        return renderNoSnapshotField(fieldId, config, isRequired);
      }

      let snapshotValue = null;
      if (fieldId === 'risk_zone') {
        snapshotValue = snapshot.risk_score ?? null;
      } else {
        const cfId = Number(fieldId.replace('custom_field_', ''));
        const fieldName = customFieldNameById[cfId];
        snapshotValue = fieldName ? (snapshot.field_values?.[fieldName]?.value ?? null) : null;
      }

      return renderHistoricalImpactField(fieldId, config, snapshotValue, isRequired);
    }

    // Special handling for cost_impact custom field - always render with CustomFieldsRenderer
    if (fieldId && fieldId.includes('custom_field_')) {
      const customFieldId = fieldId.replace('custom_field_', '');
      // Check if this is the cost_impact field by checking the field definition
      const costImpactField = customFields.find(cf => 
        cf.id === parseInt(customFieldId) && cf.name === 'cost_impact'
      );
      
      if (costImpactField) {
        // Check if fields should be editable based on current stage (same logic as other fields)
        const fieldsEditable = areFieldsEditable(stageId, fieldId);
        const isDeveloped = item?.developed === 'Developed';
        const effectivelyReadonly = isStageReadonly || !fieldsEditable || isDeveloped || isAwaitingApproval;
        
        return (
          <div key={fieldId} className="detail-field cost-impact-container">
            <CustomFieldsRenderer
              itemId={item.id}
              projectId={item.project_id || null}
              editable={!effectivelyReadonly}
              onValuesChange={(values) => {
                // The query invalidation is now handled automatically by the mutation's onSuccess callback
              }}
              // Only render the cost impact field
              filterFields={['cost_impact']}
            />
          </div>
        );
      }
    }

    // Get field configuration from centralized config
    const config = getField(fieldId);
    const isReadonly = config.type === 'readonly' || config.type === 'calculated';
    const isDeveloped = item?.developed === 'Developed';
    
    // Special handling for child_of field - show warning and use FieldRenderer for better control
    if (fieldId === 'child_of') {
      const fieldEditable = areFieldsEditable(stageId, fieldId) && !isAwaitingApproval && !isDeveloped;
      const isParentTask = directChildren.length > 0; // Check if this item has children
      
      return (
        <div key={fieldId} className="detail-field child-of-field">
          <FieldRenderer
            fieldId={fieldId}
            config={{
              ...config,
              isDisabled: !fieldEditable || isParentTask,
              tooltip: isParentTask ? 
                'This field is disabled because this item is a parent task. Parent items cannot be linked to other items to avoid circular relationships.' :
                isChildItem ? 
                  'Warning: Selecting a parent will lock this item for editing. Only the parent link can be changed after selection.' : 
                  config.tooltip
            }}
            value={fieldValue}
            isEditing={isCurrentlyEditing}
            isRequired={isRequired}
            isSaving={isSaving}
            onSave={handleFieldSave}
            onStartEdit={() => !isReadonly && fieldEditable && !isParentTask && setEditingField(fieldId)}
            onStopEdit={() => setEditingField(null)}
            additionalData={{ navigate, item, choicesData, riskMatrixConfig }}
          />
          {isParentTask && (
            <div className="parent-task-info mt-2">
              <i className="bi bi-info-circle-fill text-info me-1"></i>
              <small className="text-muted">
                This item is a parent task with {directChildren.length} child item{directChildren.length === 1 ? '' : 's'}. Parent items cannot be linked to other items to prevent circular relationships.
              </small>
            </div>
          )}
          {!isParentTask && config.type === 'multiselect' && !isReadonly && fieldEditable && (
            <div className="child-of-warning mt-2">
              <i className="bi bi-exclamation-triangle-fill text-warning me-1"></i>
              <small className="text-muted">
                Selecting a parent item will make this item read-only. All fields except this parent link will be locked.
              </small>
            </div>
          )}
        </div>
      );
    }
    
    // Special handling for risk zone field - check if required fields are present
    if (fieldId === 'risk_zone') {
      const riskValidation = checkRiskZoneRequiredFields();
      
      if (!riskValidation.canCalculate) {
        return (
          <div key={fieldId} className="detail-field">
            <label>
              {config.tooltip ? (
                <div className="tooltip-container">
                  <span>{config.label}</span>
                  <span className="tooltip-icon">i</span>
                  <div className="tooltip-content">{config.tooltip}</div>
                </div>
              ) : (
                config.label
              )}
              {isRequired && <span className="required-indicator">*</span>}
              {renderRiskMatrixInfoButton()}
            </label>
            <div className="field-value-container warning-container">
              <div className="risk-zone-warning">
                <i className="bi bi-exclamation-triangle-fill warning-icon"></i>
                <span>Enter {riskValidation.missingFields.join(', ')} to calculate risk zone</span>
              </div>
            </div>
          </div>
        );
      }
    }
    
    // Check if fields should be editable based on current stage
    const fieldsEditable = areFieldsEditable(stageId, fieldId);
    const isLifecycleComponent = config.type === 'component' && config.component === 'Lifecycle';
    const effectivelyReadonly = isReadonly || isStageReadonly || !fieldsEditable || isDeveloped || isLifecycleComponent || isAwaitingApproval;

    return (
      <div key={fieldId} className={`detail-field ${isLifecycleComponent ? 'full-width' : ''}`}>
        <label>
          {config.tooltip ? (
            <div className="tooltip-container">
              <span>{config.label}</span>
              <span className="tooltip-icon">i</span>
              <div className="tooltip-content">{config.tooltip}</div>
            </div>
          ) : (
            config.label
          )}
          {isRequired && <span className="required-indicator">*</span>}
          {fieldId === 'risk_zone' && renderRiskMatrixInfoButton()}
          {probabilityFieldId && fieldId === probabilityFieldId && renderProbabilityInfoButton()}
        </label>
        <div className={`field-value-container ${!effectivelyReadonly ? 'editable' : ''} ${isCurrentlyEditing ? 'editing' : ''} ${(config.type === 'select' || config.type === 'multiselect' || config.type === 'yesno') && !effectivelyReadonly ? 'always-editing' : ''} ${!fieldsEditable ? 'stage-locked' : ''}`}>
          {isCurrentlyEditing && !effectivelyReadonly ? (
            renderInlineEditField(fieldId, config, fieldValue)
          ) : (
            (config.type === 'select' || config.type === 'multiselect' || config.type === 'yesno') && !effectivelyReadonly ? (
              config.type === 'yesno' ? (
                <YesNoToggle
                  value={fieldValue}
                  onChange={(newValue) => handleFieldSave(fieldId, newValue)}
                  disabled={effectivelyReadonly || !fieldsEditable}
                  label={config.label}
                />
              ) : (
                renderInlineEditField(fieldId, config, fieldValue)
              )
            ) : (
              <div 
                className="field-value"
                onClick={() => !effectivelyReadonly && config.type !== 'component' && setEditingField(fieldId)}
              >
                {config.type === 'component' ? (
                  config.component === 'TasksDisplay' ? (
                    <TasksDisplay 
                      parentId={item.id}
                      parentTitle={item.title}
                      showHeader={true}
                      maxHeight="300px"
                      locked={effectivelyReadonly || !fieldsEditable}
                      includeChildTasks={directChildren.length > 0} // Include child tasks if this item has children
                    />
                  ) : config.component === 'Lifecycle' ? (
                    <Lifecycle 
                      item={item}
                      workflowStages={workflowStages.data || []}
                      tasks={itemTasks}
                      navigate={navigate}
                    />
                  ) : config.component === 'AttachmentsUpload' ? (
                    <AttachmentsUpload 
                      itemId={item.id}
                      readOnly={config.canBeReadonly === false ? false : effectivelyReadonly}
                    />
                  ) : config.component === 'CustomFieldsRenderer' ? (
                    <CustomFieldsRenderer 
                      itemId={item.id}
                      projectId={item.project_id}
                      editable={!effectivelyReadonly}
                      onValuesChange={(values) => {
                        // Handle custom field value changes if needed
                        // This could trigger re-validation or other side effects
                      }}
                    />
                  ) : (
                    <div>Unknown component: {config.component}</div>
                  )
                ) : (
                  formatDisplayValue(fieldId, fieldValue, config, { navigate, item, choicesData, riskMatrixConfig })
                )}
                {config.type === 'calculated' && config.description && (
                  <span className="calculated-field-info" title={config.description}>
                    <i className="bi bi-calculator"></i>
                  </span>
                )}
                {!fieldsEditable && !isReadonly && !isStageReadonly && (
                  <span className="stage-lock-indicator" title="Field not editable - not in current workflow stage">
                    <i className="bi bi-lock-fill"></i>
                  </span>
                )}
                {isStageReadonly && (
                  <span className="stage-lock-indicator" title="Field is read-only in this stage">
                    <i className="bi bi-eye-slash-fill"></i>
                  </span>
                )}
                {isViewerOnly && (
                  <span className="stage-lock-indicator" title="Read-only access - Viewer role">
                    <i className="bi bi-eye-fill"></i>
                  </span>
                )}
              </div>
            )
          )}
          {isSaving && (
            <div className="field-spinner">
              <i className="bi bi-arrow-clockwise spin"></i>
            </div>
          )}
        </div>
      </div>
    );
  };



  // Helper to render inline edit field
  const renderInlineEditField = (fieldId, config, currentValue) => {
    const { type, options, isClearable, isMulti } = config;
    //console.log('renderInlineEditField called for:', fieldId, 'config:', config, 'currentValue:', currentValue);

    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && type !== 'textarea' && type !== 'select' && type !== 'multiselect') {
        e.preventDefault();
        const newValue = e.target.value;
        // Convert to string for comparison since currentValue might be null/undefined
        const currentValueStr = currentValue?.toString() || '';
        const newValueStr = newValue?.toString() || '';
        
        if (newValueStr !== currentValueStr) {
          handleFieldSave(fieldId, newValue); // setEditingField(null) will be called on save success
        } else {
          setEditingField(null);
        }
      } else if (e.key === 'Escape') {
        setEditingField(null);
      }
    };

    const handleBlur = (e) => {
      if (type === 'text' || type === 'textarea' || type === 'number') {
        const newValue = e.target.value;
        // Convert to string for comparison since currentValue might be null/undefined
        const currentValueStr = currentValue?.toString() || '';
        const newValueStr = newValue?.toString() || '';
        
        if (newValueStr !== currentValueStr) {
          handleFieldSave(fieldId, newValue); // setEditingField(null) will be called on save success
        } else {
          setEditingField(null); // No changes, just exit edit mode
        }
      }
    };

    const handleSelectChange = (selectedOption) => {
      //console.log('handleSelectChange called with:', selectedOption, 'isMulti:', isMulti, 'type:', type);
      
      // Check if there's a custom onChange handler (e.g., for category field)
      if (config.onChange) {
        // Custom handler will handle the save, so don't call handleFieldSave again
        config.onChange(selectedOption);
        return;
      }
      
      let newValue;
      if (type === 'multiselect' || isMulti) {
        // For multiselect, save immediately
        newValue = selectedOption ? selectedOption.map(opt => opt.value) : [];
        console.log('Multiselect new value:', newValue);
        handleFieldSave(fieldId, newValue);
      } else {
        // For single select, save immediately
        newValue = selectedOption ? selectedOption.value : null;
        handleFieldSave(fieldId, newValue);
      }
    };



    switch (type) {
      case 'textarea':
        return (
          <textarea
            className="inline-edit-field"
            defaultValue={currentValue || ''}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            rows={3}
            autoFocus
          />
        );
      
      case 'select':
        // Find current selected option(s) with type coercion
        const currentOption = options?.find(opt => {
          // Handle type coercion for numeric IDs
          if (typeof opt.value === 'number' && typeof currentValue === 'string') {
            return opt.value === parseInt(currentValue);
          }
          if (typeof opt.value === 'string' && typeof currentValue === 'number') {
            return parseInt(opt.value) === currentValue;
          }
          return opt.value === currentValue;
        }) || null;
        
        const isOwnerField = fieldId === 'primary_owner' || fieldId === 'secondary_owner';
        const otherOwnerEmail =
          fieldId === 'primary_owner' ? item?.secondary_owner :
          fieldId === 'secondary_owner' ? item?.primary_owner :
          null;

        return (
          <Select
            className="inline-edit-select"
            value={currentOption}
            onChange={handleSelectChange}
            options={options}
            isClearable={isClearable}
            isDisabled={fieldId === 'subcategory_id' ? !currentCategoryId : (config.isDisabled || !canEditFields)}
            isOptionDisabled={(opt) => {
              if (!isOwnerField) return false;
              if (!otherOwnerEmail) return false;
              return opt?.value === otherOwnerEmail;
            }}
            placeholder={`Select ${config.label.toLowerCase()}...`}
            styles={{
              control: (provided) => ({
                ...provided,
                minHeight: '38px',
                border: 'none',
                boxShadow: 'none',
                background: '#f8f9fa',
                borderRadius: '3px'
              }),
              menu: (provided) => ({
                ...provided,
                zIndex: 9999
              }),
              option: (provided, state) => ({
                ...provided,
                color: state.isDisabled ? '#adb5bd' : provided.color,
                cursor: state.isDisabled ? 'not-allowed' : 'default'
              }),
              dropdownIndicator: (provided) => ({
                ...provided,
                color: '#6c757d'
              })
            }}
          />
        );
      
      case 'multiselect':
        // Use current value
        const currentOptions = Array.isArray(currentValue) 
          ? options?.filter(opt => currentValue.includes(opt.value)) || []
          : [];
        
        // Filter out already selected options from the dropdown
        const availableOptions = options?.filter(opt => !currentValue?.includes(opt.value)) || [];

        return (
          <Select
            className="inline-edit-select"
            value={currentOptions}
            onChange={handleSelectChange}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setEditingField(null);
              }
            }}
            options={availableOptions}
            isMulti={true}
            isClearable={isClearable}
            isSearchable={true}
            placeholder={`Select ${config.label.toLowerCase()}...`}
            closeMenuOnSelect={false} // Keep menu open for multiple selections
            hideSelectedOptions={false} // Show selected options in the dropdown
            openMenuOnClick={false} // Only open on arrow click
            openMenuOnFocus={false} // Don't open on focus
            styles={{
              control: (provided) => ({
                ...provided,
                minHeight: '38px',
                border: 'none',
                boxShadow: 'none',
                background: '#f8f9fa',
                borderRadius: '3px'
              }),
              menu: (provided) => ({
                ...provided,
                zIndex: 9999
              }),
              dropdownIndicator: (provided) => ({
                ...provided,
                color: '#6c757d'
              }),
              multiValue: (provided, state) => ({
                ...provided,
                backgroundColor: '#e3f2fd',
                borderRadius: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }),
              multiValueLabel: (provided, state) => ({
                ...provided,
                color: '#1976d2',
                fontSize: '0.875rem',
                textDecoration: 'underline',
                cursor: 'pointer'
              }),
              multiValueRemove: (provided, state) => ({
                ...provided,
                color: '#1976d2',
                cursor: 'pointer',
                ':hover': {
                  backgroundColor: '#bbdefb',
                  color: '#0d47a1'
                }
              })
            }}
            components={{
              Option: ({ innerProps, label, data, isSelected, isFocused }) => (
                <div
                  {...innerProps}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: isSelected ? '#007bff' : isFocused ? '#f8f9fa' : 'white',
                    color: isSelected ? 'white' : '#333',
                    cursor: 'pointer',
                    borderBottom: '1px solid #eee'
                  }}
                >
                  <div style={{ 
                    fontWeight: '500', 
                    marginBottom: '2px',
                    color: isSelected ? 'white' : '#007bff'
                  }}>
                    ID: {formatItemId(data.item?.id)} • {data.item?.stage_name || 'Unknown Stage'}
                  </div>
                  <div style={{ 
                    fontSize: '0.875rem', 
                    color: isSelected ? '#e6f3ff' : '#666',
                    lineHeight: '1.2'
                  }}>
                    {data.item?.title}
                  </div>
                </div>
              ),
              MultiValue: ({ data, removeProps }) => (
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  backgroundColor: '#e3f2fd',
                  borderRadius: '12px',
                  padding: '2px 8px',
                  margin: '2px',
                  fontSize: '0.875rem',
                  transition: 'all 0.2s ease'
                }}
                >
                  <span style={{ 
                    color: '#1976d2', 
                    marginRight: '4px',
                    textDecoration: 'underline',
                    cursor: 'pointer'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/items/${data.value}`);
                  }}
                  >
                    ID: {formatItemId(data.value)}
                  </span>
                  <span
                    {...removeProps}
                    style={{
                      cursor: 'pointer',
                      color: '#1976d2',
                      marginLeft: '4px',
                      fontWeight: 'bold'
                    }}
                  >
                    ×
                  </span>
                </div>
              )
            }}
          />
        );
      
      case 'number':
        return (
          <input
            type="number"
            className="inline-edit-field"
            defaultValue={currentValue || ''}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            autoFocus
          />
        );
      
      case 'text':
      default:
        return (
          <input
            type="text"
            className="inline-edit-field"
            defaultValue={currentValue || ''}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            autoFocus
          />
        );
    }
  };


  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    deleteItemMutation.mutate(id);
    setShowDeleteModal(false);
  };

  const getActiveTasksForClosure = () => {
    const parentIdsToCheck = new Set([parseInt(id)]);
    // Treat tasks on open children as blocking too (since they are still "under" this item)
    openDirectChildren.forEach(child => parentIdsToCheck.add(parseInt(child.id)));

    const isActive = (status) => {
      return !['Completed', 'Canceled'].includes(status || '');
    };

    return (allTasks || []).filter(t => parentIdsToCheck.has(parseInt(t.parent_id)) && isActive(t.status));
  };

  const handleCloseItem = async () => {
    // Check tasks first so the user doesn't type a comment and then get denied
    const activeTasks = getActiveTasksForClosure();
    if (activeTasks.length > 0) {
      setOpenTasksModal({ show: true, tasks: activeTasks });
      return;
    }

    // Then check if item has direct children that are still open
    if (openDirectChildren.length > 0) {
      setChildrenToClose(openDirectChildren);
      setShowCascadeCloseModal(true);
    } else {
      setShowCloseModal(true);
    }
  };

  const handleConfirmClose = () => {
    if (!closeComment.trim()) return;
    
    // Add close event to stage history
    const existingHistory = item?.stage_history || [];
    const closeHistoryEntry = {
      notes: `Item closed: ${closeComment}`,
      stage: "Closed",
      timestamp: new Date().toISOString(),
      user_id: currentUser?.id,
      changed_by: currentUser?.name || 'Unknown User'
    };
    const updatedHistory = [...existingHistory, closeHistoryEntry];
    
    closeItemMutation.mutate({
      itemId: id,
      updateData: { 
        status: 'closed',
        close_comment: closeComment,
        stage_history: updatedHistory,
        cascade_close: isCascadeClose // Flag for server to handle cascade
      },
      fieldId: 'status'
    });
    
    setShowCloseModal(false);
    setShowCascadeCloseModal(false);
    setCloseComment('');
    setIsCascadeClose(false);
    setChildrenToClose([]);
  };

  const handleConfirmCascadeClose = () => {
    setIsCascadeClose(true);
    setShowCascadeCloseModal(false);
    setShowCloseModal(true);
  };

  const handleCancelCascadeClose = () => {
    setShowCascadeCloseModal(false);
    setChildrenToClose([]);
    setCloseComment('');
  };

  const handleCancelClose = () => {
    setShowCloseModal(false);
    setCloseComment('');
    setIsCascadeClose(false);
    setChildrenToClose([]);
  };

  const handleReopenItem = () => {
    setShowReopenModal(true);
  };
  
  const handleConfirmReopen = async () => {
    // Add reopen event to stage history
    const existingHistory = item?.stage_history || [];
    const reopenHistoryEntry = {
      notes: "Item reopened",
      stage: "Reopened",
      timestamp: new Date().toISOString(),
      user_id: currentUser?.id,
      changed_by: currentUser?.name || 'Unknown User'
    };
    const updatedHistory = [...existingHistory, reopenHistoryEntry];
    
    // Reopen main item. Clear the close notes so an open item never shows
    // stale notes from a previous closure - the text stays in stage history.
    reopenItemMutation.mutate({
      itemId: id,
      updateData: {
        status: 'open',
        close_comment: null,
        stage_history: updatedHistory
      },
      fieldId: 'status'
    });

    // Also reopen all closed children
    const closedChildren = allItems.filter(childItem => 
      childItem.child_of && 
      Array.isArray(childItem.child_of) && 
      childItem.child_of === parseInt(id) &&
      childItem.status === 'closed'
    );

    for (const child of closedChildren) {
      const childHistory = child.stage_history || [];
      const childReopenEntry = {
        notes: "Item reopened (parent reopened)",
        stage: "Reopened",
        timestamp: new Date().toISOString(),
        user_id: currentUser?.id,
        changed_by: currentUser?.name || 'Unknown User'
      };
      const childUpdatedHistory = [...childHistory, childReopenEntry];

      updateFieldMutation.mutate({
        itemId: child.id,
        updateData: {
          status: 'open',
          close_comment: null,
          stage_history: childUpdatedHistory
        },
        fieldId: 'status'
      });
    }
    
    setShowReopenModal(false);
  };

  // Helper function to check if stage can be advanced
  const canAdvanceToNextStage = (stageId) => {
    if (!workflowStages.length) return false;

    // An unsaved draft has no row to advance; it must be saved first.
    if (isDraft) return false;

    // Child items cannot be advanced
    if (isChildItem) return false;

    // Check if this is the current stage
    if (item.current_stage !== stageId) return false;
    
    // Check if there's a next stage
    const currentStageIndex = workflowStages.findIndex(s => s.id === stageId);
    return currentStageIndex >= 0 && currentStageIndex < workflowStages.length - 1;
  };

  const finalWorkflowStage = useMemo(() => {
    if (!workflowStages || workflowStages.length === 0) return null;
    // Prefer stage_order if present
    const withOrder = workflowStages.filter(s => typeof s.stage_order === 'number');
    if (withOrder.length > 0) {
      return withOrder.reduce((max, s) => (s.stage_order > max.stage_order ? s : max), withOrder[0]);
    }
    // Fallback to last element as provided
    return workflowStages[workflowStages.length - 1];
  }, [workflowStages]);

  const isInFinalWorkflowStage = !!finalWorkflowStage && item?.current_stage === finalWorkflowStage.id;

  const isCostImpactFieldId = (fieldId) => {
    if (!fieldId || typeof fieldId !== 'string') return false;
    if (!fieldId.includes('custom_field_')) return false;
    const customFieldIdRaw = fieldId.replace('custom_field_', '').trim();
    const customFieldId = parseInt(customFieldIdRaw, 10);
    if (!Number.isFinite(customFieldId)) return false;
    return (customFields || []).some((cf) => cf && cf.id === customFieldId && cf.name === 'cost_impact');
  };

  // Helper function to check if all required fields for stage are completed
  const areRequiredFieldsComplete = (stageId) => {
    if (!stageFieldMappings || !enrichedItem) {
      return false;
    }
    
    const stageMappings = stageFieldMappings.mappings?.filter(m => m.stage_id === stageId && m.required) || [];
    const visibleRequiredMappings = stageMappings.filter(mapping => evaluateFieldVisibility(mapping, enrichedItem));
    
    for (const mapping of visibleRequiredMappings) {
      const fieldId = mapping.field_id;
      const config = getField(fieldId);
      if (!config) {
        continue;
      }
            
      // For component fields, check validation results
      if (config.type === 'component' && config.validation?.validateRequired) {
        const validationResult = componentValidationResults.get(fieldId);
        if (!validationResult?.isValid) {
          return false;
        }
      } else {
        // For regular fields, check if value exists and is not empty
        const fieldValue = enrichedItem[fieldId];        

        // cost_impact is project-dependent. Even if a score exists, it should not be considered
        // complete unless the item has a project selected.
        if (isCostImpactFieldId(fieldId)) {
          const hasProject = !!(enrichedItem?.project_id || item?.project_id);
          if (!hasProject) {
            return false;
          }
        }
        
        // Check for various empty states
        if (fieldValue === null || 
            fieldValue === undefined || 
            fieldValue === '' || 
            (Array.isArray(fieldValue) && fieldValue.length === 0)) {
          //console.log(` Field ${fieldId} is empty or null`);
          return false;
        }
      }
    }
    
    return true;
  };

  // Check if stage is ready to advance (has button and all required fields complete)
  const isReadyToAdvance = (stageId) => {
    const canAdvance = canAdvanceToNextStage(stageId);
    const fieldsComplete = areRequiredFieldsComplete(stageId);
    const result = canAdvance && fieldsComplete;
    
    return result;
  };

  // Helper function to check if we can revert to a previous stage
  const canRevertToPreviousStage = (stageId) => {
    if (!workflowStages.length || !item.current_stage) return false;
    
    // Can only revert if viewing a stage that's BEFORE the current stage
    const viewingStageIndex = workflowStages.findIndex(s => s.id === stageId);
    const currentStageIndex = workflowStages.findIndex(s => s.id === item.current_stage);
    
    return viewingStageIndex >= 0 && currentStageIndex >= 0 && viewingStageIndex < currentStageIndex;
  };

  // The endorse stage is where ownership is decided, so while an item sits there
  // the owner fields belong to the endorser alongside the endorsement fields.
  // Matched on the stage name, which is the only thing identifying the stage -
  // renaming it in admin settings carries this gate with it.
  const isEndorseStage = (stageId) => {
    const stage = workflowStages.find((s) => s.id === stageId);
    return (stage?.stage_name || '').trim().toLowerCase() === ENDORSE_STAGE_NAME;
  };

  // Helper function to check if field can be edited based on user role
  const isFieldEditableByRole = (fieldId, stageId) => {
    // Viewers cannot edit anything
    if (isViewerOnly) return false;

    const isEndorserOrAdmin = canEndorse || isAdmin;

    // Endorsement fields require endorser or admin role
    if (ENDORSEMENT_FIELD_IDS.includes(fieldId) && !isEndorserOrAdmin) {
      return false;
    }

    // Owner fields are the endorser's call, but only in the endorse stage -
    // everywhere else they stay editable by any non-viewer.
    if (OWNER_FIELD_IDS.includes(fieldId) && isEndorseStage(stageId) && !isEndorserOrAdmin) {
      return false;
    }

    // All other fields can be edited by non-viewers
    return true;
  };

  // Helper function to check if fields should be editable
  const areFieldsEditable = (stageId, fieldId) => {
    // child_of field respects current stage requirement
    if (fieldId === 'child_of') {
      // Can only edit child_of on current stage
      if (item.current_stage !== stageId) return false;
      // Closed items cannot be edited
      if (item?.status === 'closed') return false;
      return isFieldEditableByRole(fieldId, stageId);
    }

    // Check if item is a child item (locked except for child_of field)
    if (isChildItem) {
      return false;
    }

    // Closed items cannot be edited
    if (item?.status === 'closed') return false;
    
    // Fields are only editable if we're viewing the current stage AND role allows editing
    return item.current_stage === stageId && isFieldEditableByRole(fieldId, stageId);
  };

  // Helper function to get required fields for a stage
  const getStageRequiredFields = (stageId) => {
    if (!stageFieldMappings) return [];

    const stageMappings = stageFieldMappings.mappings?.filter(m => m.stage_id === stageId && m.required) || [];
    const visibleRequiredMappings = stageMappings.filter(mapping => evaluateFieldVisibility(mapping, enrichedItem));
    
    return visibleRequiredMappings.map(mapping => {
      const config = getField(mapping.field_id);
      const fieldValue = enrichedItem[mapping.field_id];
      
      // For component fields with custom validation
      if (config.type === 'component' && config.validation?.validateRequired) {
        return {
          fieldId: mapping.field_id,
          label: config.label,
          value: fieldValue,
          isComplete: false, // Will be checked asynchronously
          hasCustomValidation: true,
          config: config
        };
      }
      
      // Standard field validation
      let isComplete = fieldValue !== null && fieldValue !== undefined && fieldValue !== '';

      // cost_impact is project-dependent; do not mark complete without a selected project.
      if (isCostImpactFieldId(mapping.field_id)) {
        const hasProject = !!(enrichedItem?.project_id || item?.project_id);
        if (!hasProject) {
          isComplete = false;
        }
      }

      return {
        fieldId: mapping.field_id,
        label: config.label,
        value: fieldValue,
        isComplete,
        hasCustomValidation: false
      };
    });
  };


  // Handler to show advance stage modal (now synchronous since we use hook data)
  const handleShowAdvanceModal = () => {
    // Check if item is already approved - show approved advancement
    if (item.approval_status === 'approved') {
      setRequiresApproval(false);
      setShowingApprovedAdvance(true);
      setShowAdvanceModal(true);
      return;
    }

    // Trigger fresh validation check before showing modal
    if (activeTab && stageFieldMappings && item) {
      const stageMappings = stageFieldMappings.mappings?.filter(m => m.stage_id === activeTab && m.required) || [];
      const visibleMappings = stageMappings.filter(mapping => evaluateFieldVisibility(mapping, enrichedItem));
      const hasComponentFields = visibleMappings.some(mapping => {
        const config = getField(mapping.field_id);
        return config?.type === 'component' && config.validation?.validateRequired;
      });
      
      if (hasComponentFields && !isValidating) {
        // Clear previous validation results and validate with fresh data
        setComponentValidationResults(new Map());
        validateComponentFieldsForStage(activeTab);
        
        // Wait a bit for validation to complete before checking
        setTimeout(() => {
          if (areRequiredFieldsComplete(activeTab)) {
            // Check approval requirements
            const { requiresApproval, requirements } = checkApprovalRequirements(item.current_stage);
            setRequiresApproval(requiresApproval);
            setShowingApprovedAdvance(false);
            setApprovalRequirements(requirements);
            setShowAdvanceModal(true);
          }
        }, 100);
        return;
      }
    }
    
    // No component validation needed, check immediately
    if (canAdvanceToNextStage(activeTab) && areRequiredFieldsComplete(activeTab)) {
      const { requiresApproval, requirements } = checkApprovalRequirements(item.current_stage);
      setRequiresApproval(requiresApproval);
      setShowingApprovedAdvance(false);
      setApprovalRequirements(requirements);
      setShowAdvanceModal(true);
    }
  };

  const handleOpenWithdrawApprovalModal = useCallback(() => {
    setShowWithdrawApprovalModal(true);
  }, []);

  const handleCloseWithdrawApprovalModal = useCallback(() => {
    setShowWithdrawApprovalModal(false);
    setIsWithdrawHover(false);
  }, []);

  const handleWithdrawApproval = useCallback(async () => {
    try {
      const approvalsToDelete = pendingApprovals || [];

      for (const approval of approvalsToDelete) {
        await deleteApprovalMutation.mutateAsync({ id: approval.id, itemId: approval.item_id });
      }

      await updateFieldMutation.mutateAsync({
        itemId: id,
        updateData: { approval_status: null },
        fieldId: 'approval_status'
      });

      setToast({
        show: true,
        message: 'Approval request withdrawn. You can now make updates and resubmit when ready.',
        type: 'success'
      });

      setShowWithdrawApprovalModal(false);
      setIsWithdrawHover(false);
    } catch (error) {
      console.error('Withdraw approval error:', error);
      setToast({
        show: true,
        message: `Failed to withdraw approval: ${error?.message || 'Unknown error'}`,
        type: 'error'
      });
      throw error;
    }
  }, [pendingApprovals, deleteApprovalMutation, updateFieldMutation, id, setToast, setShowWithdrawApprovalModal, setIsWithdrawHover]);

  // Handler to close advance stage modal
  const handleCloseAdvanceModal = () => {
    setShowAdvanceModal(false);
    setAdvanceComment('');
    setRequiresApproval(false);
    setShowingApprovedAdvance(false);
    // Reset selected approvers
    setSelectedApprovers([]);
    setApprovalRequirements([]);
    setBypassApprovalSelected(false);
  };

  // Handler to proceed with stage advancement
  const handleProceedAdvance = () => {
    setShowAdvanceModal(false);
    
    if (showingApprovedAdvance) {
      // For approved items, we need to modify the advance stage to also reset status
      // We'll handle this in the handleAdvanceStage function directly by checking the flag
      handleAdvanceStage();
    } else {
      // Normal advancement (or approval request)
      handleAdvanceStage();
    }
    
    setAdvanceComment('');
    setRequiresApproval(false);
    setShowingApprovedAdvance(false);
    setBypassApprovalSelected(false);
  };

  // Handler to toggle revert panel
  const handleToggleRevertPanel = () => {
    setShowRevertPanel(!showRevertPanel);
  };

  // Handler to show revert confirmation modal
  const handleShowRevertModal = () => {
    setShowRevertPanel(false);
    setShowRevertModal(true);
  };

  // Handler to proceed with stage reversion
  const handleProceedRevert = () => {
    const stageData = {
      title: item.title || '',
      description: item.description || '',
      status: item.status || '',
      owner: item.owner || '',
      created_by: item.created_by || '',
      category_id: item.category_id || null,
      subcategory_id: item.subcategory_id || null,
      project_id: item.project_id || null,
      current_stage: activeTab
    };
    revertStageeMutation.mutate({ itemId: id, stageData, previousStageId: activeTab });
  };

  // Handler for risk snapshot delete requests
  const handleDeleteRiskSnapshot = (snapshot) => {
    setDeleteRiskSnapshotModal({ show: true, snapshot });
  };
  
  // Handler for confirming risk snapshot delete
  const handleConfirmDeleteRiskSnapshot = async () => {
    if (deleteRiskSnapshotModal.snapshot) {
      await deleteRiskSnapshotMutation.mutateAsync(deleteRiskSnapshotModal.snapshot.id);
    }
  };
  
  // Helper function to format timestamp for modal
  const formatTimestamp = (timestamp) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (error) {
      return timestamp;
    }
  };

  if (isLoading) {
    return (
      <div className="item-detail-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading item details...</p>
        </div>
      </div>
    );
  }

  // A draft legitimately has no saved row, so the not-found guard must not fire.
  if (!isDraft && (error || !item)) {
    return (
      <div className="item-detail-page">
        <div className="error-container">
          <h2>Item Not Found</h2>
          <p>The item you're looking for doesn't exist or has been deleted.</p>
          {error && (
            <div className="error-details">
              <strong>Error:</strong> {error.message}
            </div>
          )}
          <button 
            className="btn btn-primary"
            onClick={handleBackNavigation}
          >
            {source === 'reports'
              ? 'Back to Reports'
              : source === 'tasks'
                ? 'Back to Tasks'
                : source === 'task'
                  ? 'Back to Task'
                  : 'Back to Items'}
          </button>
        </div>
      </div>
    );
  }

  // Render child item warning banner
  const renderChildItemBanner = () => {
    if (!isChildItem || !parentInfo) return null;

    return (
      <div className="child-item-banner alert alert-warning d-flex align-items-center" role="alert">
        <i className="bi bi-lock-fill me-2"></i>
        <div>
          <strong>Child Item - Read Only:</strong>
          <span className="ms-1">
            This item is a child of{' '}
            <button
              className="btn btn-link"
              onClick={() => navigate(`/items/${parentInfo.parentId}`)}
              title={`Go to item ${formatItemRef(parentInfo.parentId)}`}
            >
              {formatItemRef(parentInfo.parentId)} - {parentInfo.parentTitle}
            </button>{' '}
            and cannot be edited. To make changes, you can remove the parent link using the "Child Of" field.
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className={`item-detail-page ${isMobile ? 'item-detail-page-mobile' : ''}`}>
      <div className="item-detail-container">
        {isDraft && (
          <div className="draft-item-banner alert alert-info d-flex align-items-center" role="alert">
            <i className="bi bi-pencil-square me-2"></i>
            <div>
              <strong>Draft — not saved yet:</strong>
              <span className="ms-1">
                This item does not exist until you save it. Tasks, attachments, documents,
                custom fields and approvals become available once it is saved.
              </span>
            </div>
          </div>
        )}

        {/* A draft edits title and description here directly, on every viewport.
            Which fields the stage renders below is configuration, so relying on
            that alone could leave a draft with no way to set a title and no way
            to save. */}
        {isDraft ? (
          <div className="item-header-card draft-header-card">
            <div className="item-header-field">
              <label className="item-header-label" htmlFor="draft-title">
                Title <span className="required-indicator">*</span>
              </label>
              <input
                id="draft-title"
                type="text"
                className="draft-title-input"
                value={draftItem.title || ''}
                maxLength={255}
                autoFocus
                placeholder="Give this item a title"
                onChange={(e) => setDraftItem((prev) => ({ ...prev, title: e.target.value }))}
              />
            </div>
            <div className="item-header-field">
              <label className="item-header-label" htmlFor="draft-description">Description</label>
              <textarea
                id="draft-description"
                className="draft-description-input"
                value={draftItem.description || ''}
                maxLength={2000}
                rows={3}
                placeholder="Optional. If something happens, there is a risk that... which may result in..."
                onChange={(e) => setDraftItem((prev) => ({ ...prev, description: e.target.value }))}
              />
            </div>
          </div>
        ) : !isMobile && (
          <div className="item-header-card">
            <div className="item-header-field">
              <span className="item-header-label">Title</span>
              <h2 className="item-title">{item.title || 'Untitled'}</h2>
            </div>
            {/* The Description section always shows, so the card keeps a stable
                shape whether or not the item has one. */}
            <div className="item-header-field">
              <span className="item-header-label">Description</span>
              {item.description ? (
                <ExpandableDescription className="item-description" text={item.description} />
              ) : (
                <p className="item-description item-description-empty">No Description</p>
              )}
            </div>
          </div>
        )}

        {/* Header */}
        <div className="detail-header">
          <div className="header-left">
            <button 
              className="back-btn"
              onClick={handleBackNavigation}
              title={source === 'reports'
                ? 'Back to Reports'
                : source === 'tasks'
                  ? 'Back to Tasks'
                  : source === 'task'
                    ? 'Back to Task'
                    : 'Back to Items'}
            >
              <i className="bi bi-arrow-left"></i>
            </button>
            {!isMobile && (
              <div className="header-info">
                <span className="item-id">
                  {isDraft ? 'ID: assigned on save' : `ID: ${formatItemId(item.id)}`}
                </span>
              </div>
            )}
          </div>
          
          {/* Center column: title on mobile, process flow on desktop */}
          {isMobile ? (
            <div className="mobile-header-title" title={item.title || 'Untitled'}>
              {item.title || 'Untitled'}
            </div>
          ) : (
            <div className="header-center">
              <BusinessProcessFlow
                workflowStages={workflowStages}
                currentStage={item.current_stage}
              />
            </div>
          )}
          
          <div className="header-actions">
            {isDraft && (
              <div className="draft-actions d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={handleDiscardDraft}
                  disabled={isSavingDraft}
                >
                  Discard
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveDraft}
                  disabled={!canSaveDraft}
                  title={draftTitle ? 'Save this item' : 'A title is required before saving'}
                >
                  {isSavingDraft ? 'Saving...' : 'Save Item'}
                </button>
              </div>
            )}
            {!isDraft && !isViewerOnly && (
              <div className="actions-dropdown" style={{ position: 'relative' }}>
                <button
                  className="back-btn"
                  type="button"
                  onClick={() => setShowActionsMenu(!showActionsMenu)}
                  title="Actions menu"
                  style={{
                    backgroundColor: showActionsMenu ? '#f8f9fa' : 'none'
                  }}
                >
                  <i className="bi bi-three-dots-vertical"></i>
                </button>
                {showActionsMenu && (
                  <div 
                    className="actions-menu"
                    style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #dee2e6',
                      borderRadius: '0.375rem',
                      boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
                      zIndex: 1000,
                      minWidth: '160px',
                      marginTop: '0.25rem',
                      padding: '0.5rem 0'
                    }}
                  >
                    <button
                      className="menu-item"
                      onClick={() => {
                        if (!isChildItem) {
                          setShowCreateTaskModal(true);
                          setShowActionsMenu(false);
                        }
                      }}
                      disabled={isChildItem}
                      style={{
                        width: '100%',
                        border: 'none',
                        background: 'none',
                        padding: '0.5rem 1rem',
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center',
                        cursor: isChildItem ? 'not-allowed' : 'pointer',
                        fontSize: '0.875rem',
                        opacity: isChildItem ? 0.6 : 1
                      }}
                      title={isChildItem ? 'Tasks must be created on the parent item' : ''}
                      onMouseEnter={(e) => !isChildItem && (e.target.style.backgroundColor = '#f8f9fa')}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                    >
                      <i className={`bi ${isChildItem ? 'bi-lock-fill' : 'bi-plus-circle'}`} style={{ marginRight: '0.5rem' }}></i>
                      Add Task
                    </button>
                    <hr style={{ margin: '0.5rem 0', border: 'none', borderTop: '1px solid #dee2e6' }} />
                    {item?.status === 'closed' ? (
                      <button
                        className="menu-item"
                        onClick={() => {
                          if (!isParentClosed) {
                            handleReopenItem();
                            setShowActionsMenu(false);
                          }
                        }}
                        disabled={reopenItemMutation.isLoading || isParentClosed}
                        style={{
                          width: '100%',
                          border: 'none',
                          background: 'none',
                          padding: '0.5rem 1rem',
                          textAlign: 'left',
                          display: 'flex',
                          alignItems: 'center',
                          cursor: (reopenItemMutation.isLoading || isParentClosed) ? 'not-allowed' : 'pointer',
                          fontSize: '0.875rem',
                          opacity: (reopenItemMutation.isLoading || isParentClosed) ? 0.6 : 1
                        }}
                        title={isParentClosed ? 'Cannot reopen: parent item is closed' : ''}
                        onMouseEnter={(e) => !(reopenItemMutation.isLoading || isParentClosed) && (e.target.style.backgroundColor = '#f8f9fa')}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      >
                        <i className={`bi ${isParentClosed ? 'bi-lock-fill' : 'bi-arrow-counterclockwise'}`} style={{ marginRight: '0.5rem' }}></i>
                        {isParentClosed ? 'Parent Closed' : (reopenItemMutation.isLoading ? 'Reopening...' : 'Reopen')}
                      </button>
                    ) : (
                      <button
                        className="menu-item"
                        onClick={() => {
                          if (!isChildItem) {
                            handleCloseItem();
                            setShowActionsMenu(false);
                          }
                        }}
                        disabled={closeItemMutation.isLoading || isChildItem}
                        style={{
                          width: '100%',
                          border: 'none',
                          background: 'none',
                          padding: '0.5rem 1rem',
                          textAlign: 'left',
                          display: 'flex',
                          alignItems: 'center',
                          cursor: (closeItemMutation.isLoading || isChildItem) ? 'not-allowed' : 'pointer',
                          fontSize: '0.875rem',
                          opacity: (closeItemMutation.isLoading || isChildItem) ? 0.6 : 1
                        }}
                        title={isChildItem ? 'Parent item must be closed' : ''}
                        onMouseEnter={(e) => !(closeItemMutation.isLoading || isChildItem) && (e.target.style.backgroundColor = '#f8f9fa')}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      >
                        <i className={`bi ${isChildItem ? 'bi-lock-fill' : 'bi-x-circle'}`} style={{ marginRight: '0.5rem' }}></i>
                        {isChildItem ? 'Close Parent First' : (closeItemMutation.isLoading ? 'Closing...' : 'Close')}
                      </button>
                    )}
                    <button
                      className="menu-item"
                      onClick={() => {
                        handleDelete();
                        setShowActionsMenu(false);
                      }}
                      disabled={deleteItemMutation.isLoading}
                      style={{
                        width: '100%',
                        border: 'none',
                        background: 'none',
                        padding: '0.5rem 1rem',
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center',
                        cursor: deleteItemMutation.isLoading ? 'not-allowed' : 'pointer',
                        fontSize: '0.875rem',
                        color: '#dc3545',
                        opacity: deleteItemMutation.isLoading ? 0.6 : 1
                      }}
                      onMouseEnter={(e) => !deleteItemMutation.isLoading && (e.target.style.backgroundColor = '#f8f9fa')}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                    >
                      <i className="bi bi-trash3-fill" style={{ marginRight: '0.5rem' }}></i>
                      {deleteItemMutation.isLoading ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Closed Item Banner */}
        {item?.status === 'closed' && (
          <div className="closed-item-banner">
            <i className="bi bi-check-circle-fill"></i>
            <div className="closed-item-banner-content">
              <span className="closed-item-banner-message">
                This item is closed
                {isChildItem && isParentClosed && (
                  <span className="parent-closed-note"> (parent item is also closed)</span>
                )}
              </span>
              {item.close_comment && (
                <span className="closed-item-banner-comment">
                  <span className="closed-item-banner-comment-label">Close notes:</span>
                  <span className="closed-item-banner-comment-text">{item.close_comment}</span>
                </span>
              )}
            </div>
            {!isParentClosed && (
              <button
                className="btn reopen-btn"
                onClick={handleReopenItem}
              >
                <i className="bi bi-arrow-counterclockwise"></i>
                Reopen Item
              </button>
            )}
            {isParentClosed && (
              <button
                className="btn reopen-btn"
                disabled
                title="Cannot reopen: parent item is closed"
              >
                <i className="bi bi-lock-fill"></i>
                Parent Item Closed
              </button>
            )}
          </div>
        )}

        {/* Child Item Warning Banner */}
        {renderChildItemBanner()}

        {/* Workflow Stage Tabs */}
        {workflowStages.length > 0 && (
          <div className="workflow-tabs">
            <div className="tab-header">
              {isMobile ? (
                <select
                  className="form-select mobile-tab-select"
                  value={String(activeTab)}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === 'documents' || value === 'risk-history') {
                      handleTabClick(value);
                      return;
                    }
                    const parsedStageId = Number(value);
                    handleTabClick(Number.isFinite(parsedStageId) ? parsedStageId : value);
                  }}
                  aria-label="Select workflow stage"
                >
                  {workflowStages.map((stage) => (
                    <option key={stage.id} value={String(stage.id)}>
                      {stage.stage_name}{stage.id === item.current_stage ? ' (Current)' : ''}
                    </option>
                  ))}
                  <option value="documents">Documents</option>
                  <option value="risk-history">Risk History</option>
                </select>
              ) : (
                workflowStages.map((stage) => (
                  <button
                    key={stage.id}
                    className={`tab-button ${activeTab === stage.id ? 'active' : ''} ${item.current_stage === stage.id ? 'current-stage' : ''}`}
                    onClick={() => handleTabClick(stage.id)}
                  >
                    {stage.stage_name}
                  </button>
                ))
              )}
              
              {!isMobile && (
                <>
                  {/* Documents Tab - Always Visible */}
                  <button
                    key="documents"
                    className={`tab-button ${activeTab === 'documents' ? 'active' : ''}`}
                    onClick={() => handleTabClick('documents')}
                  >
                    <i className="bi bi-file-earmark-text me-1"></i>
                    Documents
                  </button>
                  
                  {/* Risk History Tab - Always Visible */}
                  <button
                    key="risk-history"
                    className={`tab-button ${activeTab === 'risk-history' ? 'active' : ''}`}
                    onClick={() => handleTabClick('risk-history')}
                  >
                    <i className="bi bi-graph-up-arrow me-1"></i>
                    Risk History
                  </button>
                </>
              )}

              {!isMobile && <div className="tab-spacer" />}

              {(() => {
                if (isChildItem) {
                  return null;
                }

                const awaiting = isAwaitingApproval || item?.approval_status === 'awaiting';

                // Allow withdraw request button even when not viewing a stage tab.
                if (awaiting) {
                  const advanceButtonLabel = isWithdrawHover ? 'Withdraw Request' : 'Awaiting Approval';
                  const advanceButtonIcon = isWithdrawHover ? 'bi-x-circle' : 'bi-clock';
                  const advanceButtonTitle = 'Awaiting approval - click to withdraw request';
                  const advanceButtonClass = [
                    'advance-stage-tab-btn',
                    'awaiting-approval',
                    isWithdrawHover ? 'awaiting-approval-withdraw' : ''
                  ].filter(Boolean).join(' ');

                  return (
                    <button
                      className={advanceButtonClass}
                      onClick={() => handleOpenWithdrawApprovalModal()}
                      onMouseEnter={() => setIsWithdrawHover(true)}
                      onMouseLeave={() => setIsWithdrawHover(false)}
                      onFocus={() => setIsWithdrawHover(true)}
                      onBlur={() => setIsWithdrawHover(false)}
                      title={advanceButtonTitle}
                    >
                      <span>{advanceButtonLabel}</span>
                      <i className={`bi ${advanceButtonIcon}`}></i>
                    </button>
                  );
                }

                const stageId = typeof activeTab === 'number' ? activeTab : null;
                if (!stageId) {
                  return null;
                }

                if (!canAdvanceToNextStage(stageId)) {
                  return null;
                }

                const requiredComplete = areRequiredFieldsComplete(stageId);
                const isApprovedAdvance = item?.approval_status === 'approved';
                const shouldHideOnMobile = !isApprovedAdvance && !requiredComplete;

                if (isMobile && shouldHideOnMobile) {
                  return null;
                }

                const isDisabled = !isApprovedAdvance && !requiredComplete;

                const advanceButtonLabel = item?.approval_status === 'approved' ? 'Advance (Approved)' : 'Advance Stage';
                const advanceButtonIcon = item?.approval_status === 'approved' ? 'bi-check-circle' : 'bi-arrow-right';
                const advanceButtonTitle = isDisabled
                  ? 'Complete required fields to advance'
                  : (item?.approval_status === 'approved'
                    ? 'Ready to advance (approved)'
                    : 'Advance to next stage');

                const advanceButtonClass = [
                  'advance-stage-tab-btn',
                  isReadyToAdvance(stageId) ? 'pulse' : '',
                  item?.approval_status === 'approved' ? 'approved-ready' : ''
                ].filter(Boolean).join(' ');

                return (
                  <button
                    className={advanceButtonClass}
                    onClick={() => handleShowAdvanceModal()}
                    title={advanceButtonTitle}
                    disabled={isDisabled}
                  >
                    <span>{advanceButtonLabel}</span>
                    <i className={`bi ${advanceButtonIcon}`}></i>
                  </button>
                );
              })()}
              
              {/* Show disabled advance button for child items with tooltip */}
              {isChildItem && (
                <button
                  className="advance-stage-tab-btn disabled"
                  disabled={true}
                  title="Child items cannot be advanced"
                >
                  <span>Advance Stage</span>
                  <i className="bi bi-arrow-right"></i>
                </button>
              )}
              
              {/* Revert Stage Button in Tab Header */}
              {canRevertToPreviousStage(activeTab) && !isAwaitingApproval && item?.status !== 'closed' && (
                <button
                  className="revert-stage-tab-btn"
                  onClick={handleToggleRevertPanel}
                  title={showRevertPanel ? "Hide stage revert" : "Show stage revert"}
                >
                  <i className={`bi ${showRevertPanel ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                  <span>Revert to This Stage</span>
                </button>
              )}
            </div>
            
            {/* Persistent Requirements Section */}
            {canAdvanceToNextStage(activeTab) && (() => {
              const requiredFields = getStageRequiredFields(activeTab);
              let totalCount = requiredFields.length;

              // Don't show requirements section if no required fields
              if (totalCount === 0) {
                return null;
              }

              // Resolve completeness once; fields stay in their configured layout order.
              const resolvedFields = requiredFields.map((field) => {
                let isComplete = field.isComplete;
                let statusMessage = '';

                // Check if this is a component field with custom validation
                if (field.hasCustomValidation) {
                  const validationResult = componentValidationResults.get(field.fieldId);
                  if (validationResult) {
                    isComplete = validationResult.isValid;
                    statusMessage = validationResult.message;
                    // For tasks, show completed/total count
                    if (field.fieldId === 'tasks' && validationResult.details) {
                      const { completedTasks = 0, totalTasks = 0 } = validationResult.details;
                      statusMessage = `${completedTasks}/${totalTasks} complete`;
                    }
                  }
                }

                return { field, isComplete, statusMessage };
              });

              const completedCount = resolvedFields.filter(({ isComplete }) => isComplete).length;
              const isAllComplete = completedCount === totalCount;
              
              return (
                <div className="requirements-section">
                  <div className="requirements-header">
                    <h4>
                      <i className="bi bi-list-check"></i>
                      {isAllComplete 
                        ? "Requirements complete - ready to advance"
                        : `Required before advancing (${completedCount}/${totalCount})`
                      }
                    </h4>
                    <button 
                      className={`requirements-toggle ${requirementsCollapsed ? 'collapsed' : ''}`}
                      onClick={() => setRequirementsCollapsed(!requirementsCollapsed)}
                      type="button"
                    >
                      <i className="bi bi-chevron-up"></i>
                    </button>
                  </div>
                  
                  <div className={`requirements-content ${requirementsCollapsed ? 'collapsed' : 'expanded'}`}>
                    <div className="requirements-list">
                      {resolvedFields.map(({ field, isComplete, statusMessage }) => {
                        const displayText = field.fieldId === 'tasks' && statusMessage
                          ? `${field.label} (${statusMessage})`
                          : field.label;

                        return (
                          <div key={field.fieldId} className={`requirement-item ${isComplete ? 'complete' : 'incomplete'}`}>
                            <div className="requirement-icon">
                              {isComplete ? (
                                <i className="bi bi-check-circle-fill"></i>
                              ) : (
                                <i className="bi bi-x-circle-fill"></i>
                              )}
                            </div>
                            <div className="requirement-info">
                              <span className="requirement-label">{displayText}</span>
                            </div>
                          </div>
                        );
                      })}

                      {requiredFields.length === 0 && (
                        <div className="no-requirements">
                          <i className="bi bi-info-circle"></i>
                          <p>No required fields configured for this stage.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}
            
            {/* Collapsible Revert Panel */}
            {canRevertToPreviousStage(activeTab) && !isAwaitingApproval && item?.status !== 'closed' && (
              <div className={`revert-panel ${showRevertPanel ? 'expanded' : 'collapsed'}`}>
                <div className="revert-panel-content">
                  <div className="revert-panel-header">
                    <h4>
                      <i className="bi bi-arrow-left-circle text-warning"></i>
                      Revert to Previous Stage
                    </h4>
                  </div>
                  
                  <div className="revert-warning">
                    <i className="bi bi-exclamation-triangle-fill text-warning"></i>
                    <div>
                      <p><strong>Warning:</strong> You are about to revert this item to an earlier stage in the workflow.</p>
                      <p>This will change the item's current stage from <strong>{workflowStages.find(s => s.id === item.current_stage)?.stage_name}</strong> back to <strong>{workflowStages.find(s => s.id === activeTab)?.stage_name}</strong>.</p>
                      <p>This action may affect the workflow progress and should only be done if changes are needed in this earlier stage.</p>
                    </div>
                  </div>
                  
                  <div className="revert-panel-actions">
                    <button className="btn btn-secondary" onClick={handleToggleRevertPanel}>
                      Cancel
                    </button>
                    <button 
                      className="btn btn-warning"
                      onClick={handleShowRevertModal}
                      disabled={isViewerOnly}
                      title={isViewerOnly ? "Viewers cannot revert workflow stages" : ""}
                    >
                      <i className="bi bi-arrow-left"></i>
                      Revert to This Stage
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Stage Warning Banner - show when not viewing current stage, but not for documents tab */}
            {activeTab !== item.current_stage && item.current_stage && item?.status !== 'closed' && activeTab !== 'documents' && activeTab !== 'risk-history' && typeof activeTab === 'number' && (
              <div className="stage-warning">
                <i className="bi bi-exclamation-triangle stage-warning-icon"></i>
                <div className="stage-warning-content">
                  <div className="stage-warning-message">
                    You are not viewing the current stage. The current stage is <strong>{workflowStages.find(s => s.id === item.current_stage)?.stage_name}</strong>.
                  </div>
                  <button 
                    className="stage-warning-action"
                    onClick={() => handleTabClick(item.current_stage)}
                  >
                    Go to Current Stage
                  </button>
                </div>
              </div>
            )}

            {/* Final Stage Notice */}
            {isInFinalWorkflowStage && item?.status !== 'closed' && activeTab !== 'documents' && activeTab !== 'risk-history' && typeof activeTab === 'number' && (
              <div className="stage-warning">
                <i className="bi bi-info-circle stage-warning-icon"></i>
                <div className="stage-warning-content">
                  <div className="stage-warning-message">
                    This item is in the final workflow stage, if you would like to fully close this item, click here
                  </div>
                  <button
                    className="stage-warning-action"
                    onClick={handleCloseItem}
                  >
                    Close Item
                  </button>
                </div>
              </div>
            )}
            
            <div className="tab-content">
              {workflowStages.map((stage) => (
                <div
                  key={stage.id}
                  className={`tab-pane ${activeTab === stage.id ? 'active' : ''}`}
                >
                  <div className="stage-content">

                    {/* Item Details for Current/Active Tab */}
                    {activeTab === stage.id && (
                      <>
                        <div className="stage-content-wrapper">
                          <div className="dynamic-stage-content">
                            {(() => {
                              const stageStructure = getStageFieldStructure(stage.id);
                              const stageLayout = getStageLayout(stage.id);
                              
                              if (stageStructure.subsections.length === 0 && stageStructure.fieldsWithoutSubsection.length === 0) {
                                return (
                                  <div className="no-fields-message">
                                    <p>No fields configured for this stage.</p>
                                  </div>
                                );
                              }

                              return (
                                <div className={`stage-sections-grid ${stageLayout === 3 ? 'three-column' : ''}`}>
                                  {/* Render Subsections */}
                                  {stageStructure.subsections.map((subsection) => (
                                    <div
                                      key={subsection.id}
                                      className={`detail-section ${
                                        subsection.columns === 3 ? 'full-width' :
                                        subsection.columns === 2 ? 'full-width' : 'half-width'
                                      }`}
                                    >
                                      <h4>{subsection.title}</h4>
                                      <div className={`detail-grid ${
                                        subsection.columns === 3 ? 'three-column' :
                                        subsection.columns === 2 ? 'two-column' : 'one-column'
                                      }`}>
                                        {subsection.fields.map((fieldMapping) => renderField(fieldMapping, stage.id))}
                                      </div>
                                    </div>
                                  ))}

                                  {/* Render Fields Without Subsection */}
                                  {stageStructure.fieldsWithoutSubsection.length > 0 && (
                                    <div className="detail-section full-width">
                                      <h4>Other Fields</h4>
                                      <div className="detail-grid two-column">
                                        {stageStructure.fieldsWithoutSubsection.map((fieldMapping) => renderField(fieldMapping, stage.id))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </div>


                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
              
              {/* Documents Tab Pane */}
              <div
                key="documents"
                className={`tab-pane ${activeTab === 'documents' ? 'active' : ''}`}
              >
                <div className="stage-content">
                  <div className="stage-content-wrapper">
                    {isDraft ? (
                      <div className="draft-locked-panel text-muted">
                        <i className="bi bi-lock-fill me-2"></i>
                        Documents can be added once the item is saved.
                      </div>
                    ) : (
                      <DocumentsDisplay
                        parentId={item.id}
                        parentType="item"
                        showHeader={true}
                        maxHeight="600px"
                      />
                    )}
                  </div>
                </div>
              </div>
              
              {/* Risk History Tab Pane */}
              <div
                key="risk-history"
                className={`tab-pane ${activeTab === 'risk-history' ? 'active' : ''}`}
              >
                <div className="stage-content">
                  <div className="stage-content-wrapper">
                    {isDraft ? (
                      <div className="draft-locked-panel text-muted">
                        <i className="bi bi-lock-fill me-2"></i>
                        Risk history starts once the item is saved.
                      </div>
                    ) : (
                      <RiskAssessmentTimeline itemId={item.id} onDeleteSnapshot={handleDeleteRiskSnapshot} />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <Toast 
          show={toast.show}
          message={toast.message}
          type={toast.type}
          onClose={handleCloseToast}
        />

        {/* Delete Confirmation Modal */}
        <Modal
          show={showDeleteModal}
          title="Confirm Delete"
          onCancel={() => setShowDeleteModal(false)}
          onConfirm={handleConfirmDelete}
          confirmText="Delete"
          cancelText="Cancel"
          confirmBtnClass="btn-danger"
          loading={deleteItemMutation.isLoading}
          content={
            <div className="delete-modal-content">
              <div className="alert alert-warning">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                <strong>Note:</strong> This item will be moved to the trashcan where it can be recovered for 30 days.
              </div>
              <p className="mb-0">
                Are you sure you want to delete item <strong>{formatItemRef(id)}</strong>?
              </p>
              {item?.title && (
                <p className="text-muted mb-0">
                  <strong>Title:</strong> {item.title}
                </p>
              )}
            </div>
          }
        />

        {/* Owner Assignment Confirmation Modal */}
        <Modal
          show={showOwnerAssignmentModal}
          title={`Assign ${pendingOwnerRoleLabel}`}
          onCancel={handleCancelOwnerAssignment}
          content={
            <div className="owner-assignment-modal-content">
              <p className="mb-3">
                You are about to set the <strong>{pendingOwnerRoleLabel}</strong> of{' '}
                <strong>{isDraft ? 'this new item' : formatItemRef(id)}</strong> to:
              </p>

              <div className="owner-assignment-target">
                <i className="bi bi-person-fill me-2"></i>
                <strong>{pendingOwnerDisplay}</strong>
              </div>

              {pendingOwnerReplaces && (
                <p className="text-muted mt-3 mb-0">
                  <i className="bi bi-arrow-left-right me-1"></i>
                  This replaces the current {pendingOwnerRoleLabel.toLowerCase()},{' '}
                  {pendingOwnerReplaces}.
                </p>
              )}

              <div className="alert alert-info mt-3 mb-0">
                <i className="bi bi-envelope-fill me-2"></i>
                <strong>Confirm &amp; Notify</strong> assigns them and emails them about it.
                {' '}<strong>Confirm Without Notifying</strong> makes the same assignment
                {' '}silently.
                {isDraft && ' Either way, nothing is saved or sent until you save this item.'}
              </div>
            </div>
          }
          footer={
            // Wrapped so three buttons wrap instead of overflowing on narrow screens.
            <div className="owner-assignment-modal-footer">
              <button
                className="btn btn-secondary"
                onClick={handleCancelOwnerAssignment}
                style={ownerAssignmentBtnStyle}
              >
                Cancel
              </button>
              <button
                className="btn btn-outline-primary"
                onClick={() => handleConfirmOwnerAssignment(false)}
                style={ownerAssignmentBtnStyle}
              >
                <i className="bi bi-bell-slash me-2"></i>
                Confirm Without Notifying
              </button>
              <button
                className="btn btn-primary"
                onClick={() => handleConfirmOwnerAssignment(true)}
                style={ownerAssignmentBtnStyle}
              >
                <i className="bi bi-envelope me-2"></i>
                Confirm &amp; Notify
              </button>
            </div>
          }
        />

        {/* Parent Relationship Confirmation Modal */}
        <Modal
          show={showParentRelationshipModal}
          title="Establish Parent Relationship"
          onCancel={handleCancelParentRelationship}
          onConfirm={handleConfirmParentRelationship}
          confirmText="Establish Relationship"
          cancelText="Cancel"
          confirmBtnClass="btn-warning"
          size="lg"
          content={
            <div className="parent-relationship-modal-content">
              <div className="alert alert-warning">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                <strong>Warning:</strong> Setting a parent relationship will lock this item for editing.
              </div>
              
              <p className="mb-3">
                You are about to set this item as a child of:
              </p>
              
              <div className="parent-list-container">
                <div className="parent-list">
                  {pendingParentTitles.map((parentTitle, index) => (
                    <div key={index} className="parent-item-entry">
                      <div className="parent-item-info">
                        <i className="bi bi-link me-2"></i>
                        <span className="parent-item-title">{parentTitle}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="parent-relationship-warning">
                <p className="text-muted mb-2">
                  <i className="bi bi-info-circle me-1"></i>
                  After establishing this relationship:
                </p>
                <ul className="text-muted mb-0">
                  <li>All fields except the "Child Of" field will become read-only</li>
                  <li>To edit this item, you must first remove the parent relationship</li>
                  <li>Tasks can still be created and managed normally</li>
                </ul>
              </div>
              
              <p className="mt-3">
                Do you want to proceed with establishing this parent relationship?
              </p>
            </div>
          }
        />

        {/* Cascade Close Confirmation Modal */}
        <Modal
          show={showCascadeCloseModal}
          title="Close Parent Item with Children"
          onCancel={handleCancelCascadeClose}
          onConfirm={handleConfirmCascadeClose}
          confirmText="Proceed to Close All"
          cancelText="Cancel"
          confirmBtnClass="btn-warning"
          size="lg"
          content={
            <div className="cascade-close-modal-content">
              <div className="alert alert-warning">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                <strong>Warning:</strong> This item has child items that are still open.
              </div>
              
              <p className="mb-3">
                Closing this parent item will also automatically close <strong>{childrenToClose.length}</strong> child item{childrenToClose.length !== 1 ? 's' : ''}:
              </p>
              
              <div className="children-list-container">
                <div className="children-list">
                  {childrenToClose.map((child) => (
                    <div key={child.id} className="child-item-entry">
                      <div className="child-item-info">
                        <span className="child-item-id">{formatItemRef(child.id)}</span>
                        <span className="child-item-title">{child.title || 'No title'}</span>
                        <span className="child-item-stage">{child.current_stage_name || 'No stage'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="cascade-warning">
                <p className="text-muted">
                  <i className="bi bi-info-circle me-1"></i>
                  All child items will receive the same close comment and timestamp as the parent item.
                </p>
              </div>
              
              <p className="mt-3">
                Do you want to proceed with closing this item and all its children?
              </p>
            </div>
          }
        />

        {/* Close Item Modal */}
        <Modal
          show={showCloseModal}
          title="Close Item"
          onCancel={handleCancelClose}
          onConfirm={handleConfirmClose}
          confirmText="Close Item"
          cancelText="Cancel"
          confirmBtnClass="btn-primary"
          loading={closeItemMutation.isLoading}
          confirmDisabled={!closeComment.trim()}
          content={
            <div className="close-modal-content">
              <div className="alert alert-warning">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                <strong>Notice:</strong> This will close the item and hide it from the main items list. You can reopen it later if needed.
              </div>
              
              {isCascadeClose && (
                <div className="alert alert-info">
                  <i className="bi bi-info-circle-fill me-2"></i>
                  <strong>Cascade Close:</strong> This will also close {childrenToClose.length} child item{childrenToClose.length !== 1 ? 's' : ''}.
                </div>
              )}
              
              <p>
                Are you sure you want to close item <strong>{formatItemRef(id)}</strong>?
              </p>
              {item?.title && (
                <p className="text-muted mb-3">
                  <strong>Title:</strong> {item.title}
                </p>
              )}
              <div className="close-comment-section">
                <label htmlFor="close-comment">
                  Reason for closing (required):
                </label>
                <textarea
                  id="close-comment"
                  className="form-control"
                  rows={4}
                  placeholder={`Please provide a reason for closing this item${isCascadeClose ? ' and its children' : ''}...`}
                  value={closeComment}
                  onChange={(e) => setCloseComment(e.target.value)}
                  required
                />
              </div>
            </div>
          }
        />

        {/* Cannot Close Item (Active Tasks) Modal */}
        <Modal
          show={openTasksModal.show}
          title="Cannot Close Item"
          onCancel={() => setOpenTasksModal({ show: false, tasks: [] })}
          cancelText="Close"
          className="close-tasks-modal"
          footer={
            <button
              className="btn btn-secondary"
              onClick={() => setOpenTasksModal({ show: false, tasks: [] })}
            >
              Close
            </button>
          }
          content={
            <div>
              <div className="alert alert-warning">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                <strong>Action required:</strong> Close the following task{openTasksModal.tasks.length !== 1 ? 's' : ''} before closing this item.
              </div>

              {openTasksModal.tasks.length === 0 ? (
                <p className="mb-0 text-muted">No task details were returned.</p>
              ) : (
                <div className="close-tasks-list">
                  {openTasksModal.tasks.map((task) => (
                    <div key={task.id} className="card close-task-card border">
                      <div className="card-body">
                        <div className="close-task-row">
                          <div className="close-task-main" style={{ minWidth: 0 }}>
                            <div className="d-flex align-items-start flex-wrap">
                              <span className="close-task-title">Task #{task.id}</span>
                              {task.status ? (
                                <span className="badge bg-secondary close-task-status">{task.status}</span>
                              ) : null}
                            </div>

                            {task.description ? (
                              <div className="text-muted mt-1 close-task-description">
                                {task.description}
                              </div>
                            ) : null}

                            {task.parent_title ? (
                              <div className="text-muted small mt-1">Item: {task.parent_title}</div>
                            ) : null}
                          </div>

                          <div className="close-task-actions">
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => {
                                setOpenTasksModal({ show: false, tasks: [] });
                                navigate(`/tasks/${task.id}?source=item&sourceItemId=${id}`);
                              }}
                            >
                              <i className="bi bi-box-arrow-up-right me-1"></i>
                              Open
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          }
        />

        {/* Reopen Item Modal */}
        <Modal
          show={showReopenModal}
          title="Reopen Item"
          onCancel={() => setShowReopenModal(false)}
          onConfirm={handleConfirmReopen}
          confirmText="Reopen Item"
          cancelText="Cancel"
          confirmBtnClass="btn-success"
          loading={reopenItemMutation.isLoading}
          content={
            <div className="reopen-modal-content">
              <div className="alert alert-info">
                <i className="bi bi-info-circle-fill me-2"></i>
                <strong>Notice:</strong> This will reopen the item and make it visible in the main items list again.
              </div>
              <p>
                Are you sure you want to reopen item <strong>{formatItemRef(id)}</strong>?
              </p>
              {item?.title && (
                <p className="text-muted mb-3">
                  <strong>Title:</strong> {item.title}
                </p>
              )}
              {item?.owner && (
                <p className="mb-0">
                  <strong>The owner ({item.owner}) will be notified of this change.</strong>
                </p>
              )}
            </div>
          }
        />

        {/* Revert Stage Confirmation Modal */}
        <Modal
          show={showRevertModal}
          title="Confirm Stage Revert"
          onCancel={() => setShowRevertModal(false)}
          onConfirm={handleProceedRevert}
          confirmText="Revert Stage"
          cancelText="Cancel"
          confirmBtnClass="btn-warning"
          loading={revertStageeMutation.isPending}
          content={
            <div className="revert-modal-content">
              <div className="alert alert-warning">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                <strong>Warning:</strong> This will change the workflow stage.
              </div>
              <p>
                You are about to revert item <strong>{formatItemRef(id)}</strong> from:
              </p>
              <div className="stage-change-preview">
                <div className="current-stage">
                  <strong>Current Stage:</strong> {workflowStages.find(s => s.id === item.current_stage)?.stage_name || 'Unknown'}
                </div>
                <div className="arrow">
                  <i className="bi bi-arrow-down"></i>
                </div>
                <div className="target-stage">
                  <strong>Reverting to:</strong> {workflowStages.find(s => s.id === activeTab)?.stage_name || 'Unknown'}
                </div>
              </div>
              <p className="mt-3">
                This will allow you to make changes to fields in the earlier stage. 
                Are you sure you want to proceed?
              </p>
            </div>
          }
        />

        {/* Withdraw Approval Modal */}
        <Modal
          show={showWithdrawApprovalModal}
          title="Withdraw Approval Request"
          onCancel={handleCloseWithdrawApprovalModal}
          onConfirm={handleWithdrawApproval}
          confirmText="Withdraw Approval"
          cancelText="Keep Approval"
          confirmBtnClass="btn-danger"
          className="withdraw-approval-modal"
          content={
            <div className="withdraw-approval-modal-content">
              <div className="alert alert-warning mb-3">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                <strong>Warning:</strong> Withdrawing this request will stop the current approval process.
                <ul className="mt-2 mb-0">
                  <li>The pending approval record will be removed.</li>
                  <li>Assigned approvers will be notified that the request was cancelled.</li>
                  <li>You will need to resubmit an approval request before advancing again.</li>
                </ul>
              </div>
              {pendingApprovals.length > 0 && (
                <div className="pending-approvals-summary mb-3">
                  <p className="mb-1"><strong>Pending approvals:</strong></p>
                  <ul className="mb-0">
                    {pendingApprovals.map((approval) => (
                      <li key={approval.id}>
                        {approval.assignment_display || 'Approval request'}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <p className="mb-0">
                After withdrawing, you can make further edits and submit a fresh approval request when ready.
              </p>
            </div>
          }
        />

        {/* Advance Stage Modal */}
        <Modal
          show={showAdvanceModal}
          title={showingApprovedAdvance ? "Advance Approved Item" : (requiresApproval ? (item?.approval_status === 'denied' ? "Resubmit Stage Approval" : "Request Stage Approval") : "Advance to Next Stage")}
          onCancel={handleCloseAdvanceModal}
          onConfirm={handleProceedAdvance}
          confirmText={showingApprovedAdvance
            ? "Advance"
            : (requiresApproval
              ? (bypassApprovalSelected && canBypassApprovalByRole
                ? "Advance Stage"
                : (item?.approval_status === 'denied' ? "Resubmit Approval" : "Request Approval"))
              : "Advance Stage")}
          cancelText="Cancel"
          confirmButtonClass={showingApprovedAdvance ? "btn-success" : (requiresApproval ? "btn-warning" : "btn-success")}
          confirmDisabled={(() => {
            const isMissingOwnerApproval =
              !bypassApprovalSelected &&
              requiresApproval &&
              (hasItemOwnerRequirement || hasItemSecondaryOwnerRequirement) &&
              missingRequiredOwners.length > 0;

            // Role-based requirements require at least one selected approver.
            const isMissingApprovers =
              !bypassApprovalSelected &&
              requiresApproval &&
              hasRealRoleRequirement &&
              selectedApprovers.length === 0;

            return isMissingOwnerApproval || isMissingApprovers;
          })()}
          content={
            <div className="advance-modal-content">
              {showingApprovedAdvance ? (
                // Show approval details for already approved items
                <div>
                  <p>
                    <strong>Item Approved - Ready to Advance</strong>
                  </p>
                  {approvedApprovals.length > 0 && (
                    <div className="approval-details">
                      <p><strong>Approved by:</strong> {approvedApprovals[0].approved_by_user}</p>
                      <p><strong>Approved on:</strong> {new Date(approvedApprovals[0].updated_at).toLocaleDateString()}</p>
                      {approvedApprovals[0].comment && (
                        <p><strong>Approval Comment:</strong> {approvedApprovals[0].comment}</p>
                      )}
                    </div>
                  )}
                  <p>
                    This item will advance from <strong>{workflowStages.find(s => s.id === item.current_stage)?.stage_name}</strong> to the next stage.
                  </p>
                  <div className="alert alert-info mt-3">
                    <i className="bi bi-info-circle me-2"></i>
                    <strong>Note:</strong> After advancing, the item status will be reset to Active for the next stage.
                  </div>
                </div>
              ) : requiresApproval ? (
                // Approval required content
                <div className="approval-modal">
                  {/* Show denial information if item was previously denied */}
                  {item?.approval_status === 'denied' && deniedApprovals.length > 0 && (
                    <div className="alert alert-danger approval-modal__alert">
                      <i className="bi bi-x-circle-fill me-2"></i>
                      <strong>Previous approval was denied:</strong>
                      <div className="mt-2">
                        {deniedApprovals.map((denial, index) => (
                          <div key={index} className={index < deniedApprovals.length - 1 ? 'mb-2' : ''}>
                            <p className="mb-0 fw-semibold">
                              Denied by: {denial.approved_by_user || 'Unknown'}
                            </p>
                            {denial.comment && (
                              <p className="mt-1 mb-0 fst-italic">
                                Reason: {denial.comment}
                              </p>
                            )}
                            {denial.updated_at && (
                              <p className="mt-1 mb-0 text-muted small">
                                On: {new Date(denial.updated_at).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="approval-modal__section">
                    <p className="mb-2">
                      This stage requires approval before advancing from <strong>{workflowStages.find(s => s.id === item.current_stage)?.stage_name}</strong> to the next stage:
                    </p>

                  {(hasItemOwnerRequirement || hasItemSecondaryOwnerRequirement) && (
                    <div className="alert alert-info approval-modal__alert">
                      <i className="bi bi-info-circle me-2"></i>
                      <strong>Item Owners:</strong>
                      <div className="mt-2">
                        <div>
                          <strong>Item Owner:</strong> {primaryOwnerDisplay || <span className="text-danger">Not set</span>}
                        </div>
                        <div>
                          <strong>Item Secondary Owner:</strong> {secondaryOwnerDisplay || <span className="text-danger">Not set</span>}
                        </div>
                      </div>
                      {missingRequiredOwners.length > 0 && (
                        <div className="mt-2">
                          <span className="text-danger">
                            Missing required: {missingRequiredOwners.join(', ')}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                    <div className="approval-roles-list">
                      {(approvalRequirements || []).map((req, index) => (
                        <div key={index} className="approval-role-item">
                          <i className="bi bi-shield-check text-warning me-2"></i>
                          <strong>{req.role_name}</strong>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Approver selection: pick who to notify from the assigned role(s) */}
                  {hasRealRoleRequirement && (
                    <div className="approval-modal__section approver-selection">
                      <div className="approval-modal__section-title">Notify</div>
                      <label className="form-label mb-1">
                        <strong>Select approvers:</strong>
                      </label>
                      <Select
                        isMulti
                        value={selectedApprovers}
                        onChange={(value) => setSelectedApprovers(value || [])}
                        options={approverOptions}
                        placeholder="Choose who to notify for approval..."
                        isSearchable
                        closeMenuOnSelect={false}
                        noOptionsMessage={() => 'No eligible approvers found for the assigned role'}
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: base => ({ ...base, zIndex: 9999 }),
                          control: (provided, state) => ({
                            ...provided,
                            borderColor: state.isFocused ? 'var(--nsi-brand)' : '#ced4da',
                            boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(var(--nsi-brand-rgb), 0.25)' : 'none',
                            '&:hover': {
                              borderColor: 'var(--nsi-brand)'
                            }
                          })
                        }}
                      />
                      <small className="form-text text-muted d-block mt-1">
                        Only the people you select will be emailed and allowed to approve — not everyone in the role.
                      </small>
                      {selectedApprovers.length === 0 && (
                        <small className="text-danger d-block mt-1">
                          <i className="bi bi-exclamation-triangle me-1"></i>
                          Select at least one approver to notify
                        </small>
                      )}
                    </div>
                  )}

                  {canBypassApprovalByRole && (
                    <div className="approval-modal__section">
                      <div className="approval-modal__section-title">Bypass</div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="bypassApprovalSelected"
                          checked={bypassApprovalSelected}
                          onChange={(e) => setBypassApprovalSelected(e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="bypassApprovalSelected">
                          <strong>You have permission to advance this item without approval</strong>
                        </label>
                        <small className="form-text text-muted d-block ms-4">
                          If selected, the item will advance immediately and no approval request will be created.
                        </small>
                      </div>
                    </div>
                  )}
                  
                  <div className="alert alert-warning approval-modal__alert">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    <strong>Warning:</strong> {bypassApprovalSelected && canBypassApprovalByRole
                      ? 'Advancing without approval will skip the approval workflow for this transition.'
                      : (item?.approval_status === 'denied'
                        ? 'Resubmitting approval will lock editing until the approval is granted or denied again.'
                        : 'Requesting approval will lock editing until the approval is granted or denied.')}
                  </div>
                </div>
              ) : (
                // Normal advancement content
                <div>
                  <p>
                    <strong>Confirm Stage Advancement</strong>
                  </p>
                  <p>
                    This will advance the item from <strong>{workflowStages.find(s => s.id === item.current_stage)?.stage_name}</strong> to the next stage.
                  </p>
                  
                  <div className="required-fields-summary">
                    <h5>Required fields completed:</h5>
                    <div className="requirements-list-modal">
                      {getStageRequiredFields(activeTab).map((field) => {
                        let isComplete = field.isComplete;
                        
                        // Check if this is a component field with custom validation
                        if (field.hasCustomValidation) {
                          const validationResult = componentValidationResults.get(field.fieldId);
                          if (validationResult) {
                            isComplete = validationResult.isValid;
                          }
                        }
                        
                        return (
                          <div key={field.fieldId} className={`requirement-item-modal ${isComplete ? 'complete' : 'incomplete'}`}>
                            <div className="requirement-icon">
                              {isComplete ? (
                                <i className="bi bi-check-circle-fill text-success"></i>
                              ) : (
                                <i className="bi bi-x-circle-fill text-danger"></i>
                              )}
                            </div>
                            <span className="requirement-label">{field.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              <div className="advance-comment-section mt-3">
                <label htmlFor="advance-comment" className="form-label">
                  <strong>{requiresApproval ? (item?.approval_status === 'denied' ? "Resubmission Comment:" : "Approval Request Comment:") : "Comment (optional):"}</strong>
                </label>
                <textarea
                  id="advance-comment"
                  className="form-control advance-comment-textarea"
                  rows="3"
                  placeholder={requiresApproval ? 
                    (item?.approval_status === 'denied' ? "Add a comment explaining changes made for resubmission..." : "Add a comment for the approval request...") : 
                    "Add a comment about this stage advancement..."
                  }
                  value={advanceComment}
                  onChange={(e) => setAdvanceComment(e.target.value)}
                />
              </div>
            </div>
          }
        />

        {/* Create Task Modal */}
        <CreateTaskModal 
          show={showCreateTaskModal}
          onClose={() => setShowCreateTaskModal(false)}
          preSelectedParentId={item?.id}
          preSelectedParentTitle={item?.title}
          onTaskCreated={(message, type) => showToast(message, type)}
        />

        {/* Risk Matrix Modal */}
        <RiskMatrixModal
          show={showRiskMatrixModal}
          onClose={() => setShowRiskMatrixModal(false)}
          riskMatrixConfig={riskMatrixConfig}
          customFields={customFields}
          currentScore={enrichedItem?.risk_zone}
        />

        {/* Probability Scale Modal */}
        <ProbabilityScaleModal
          show={showProbabilityModal}
          onClose={() => setShowProbabilityModal(false)}
          probabilityField={probabilityField}
          currentValue={probabilityFieldId ? enrichedItem?.[probabilityFieldId] : null}
        />

        {/* Delete Risk Snapshot Modal */}
        <Modal
          show={deleteRiskSnapshotModal.show}
          onCancel={() => setDeleteRiskSnapshotModal({ show: false, snapshot: null })}
          onConfirm={handleConfirmDeleteRiskSnapshot}
          title="Delete Risk Snapshot"
          content={
            <div>
              <p>Are you sure you want to delete this risk snapshot?</p>
              {deleteRiskSnapshotModal.snapshot && (
                <div className="snapshot-preview">
                  <strong>Stage:</strong> {deleteRiskSnapshotModal.snapshot.stage_name || 'Unknown Stage'}<br />
                  <strong>Risk Score:</strong> {deleteRiskSnapshotModal.snapshot.risk_score}<br />
                  <strong>Date:</strong> {formatTimestamp(deleteRiskSnapshotModal.snapshot.captured_at)}<br />
                  <strong>Type:</strong> {deleteRiskSnapshotModal.snapshot.snapshot_type?.replace('_', ' ')}
                </div>
              )}
              <p className="text-danger">This action cannot be undone.</p>
            </div>
          }
          confirmText="Delete"
          cancelText="Cancel"
          confirmBtnClass="btn-danger"
          loading={deleteRiskSnapshotMutation.isPending}
        />

      </div>
    </div>
  );
};

export default ItemDetailPage;